import {
  Connection,
  PublicKey,
  Transaction,
  Keypair,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import { BN } from 'bn.js';
import { decode as decodeBase58 } from 'bs58';
import {
  DynamicBondingCurveClient,
  deriveDbcPoolAddress,
  getPriceFromSqrtPrice,
  calculateQuoteToBaseFromAmountIn,
  TokenDecimal,
} from '@meteora-ag/dynamic-bonding-curve-sdk';
import type {
  CreatePoolInput,
  CreatePoolResult,
  PoolInfo,
  BuyFromCurveInput,
  SellToCurveInput,
  TradeResult,
  TradeQuote,
} from './types';
import { getSolscanUrl, getSolanaEndpoint, getSolanaWsEndpoint } from '@/lib/solana/config';
import { 
  DEXTORO_TREASURY_WALLET, 
  WSOL_MINT, 
  getTreasuryWsolAta,
  getReferralAccountForMint,
} from '@/lib/solana/treasury';
import {
  MEMELAUNCH_TREASURY_PUBKEY,
  CREATOR_TRADING_FEE_PERCENTAGE,
  PARTNER_TRADING_FEE_PERCENTAGE,
  CREATOR_MIGRATION_FEE_PERCENTAGE,
  PARTNER_MIGRATION_FEE_PERCENTAGE,
  validateFeeConfig,
  logFeeConfigSummary,
  type ConfigValidationResult,
} from './feeConfig';
import {
  sendAndConfirmWithFreshBlockhash,
  type TransactionProgress,
} from '@/lib/solana/transactionHelper';
import { confirmSignatureHttp, tradingPollInterval } from '@/lib/solana/confirmSignatureHttp';
import { MIGRATION_FDV_SOL_BY_CONFIG } from '@/lib/meteora/graduation';
import { prepareTransactionForSender } from '@/lib/trading/sender';
import * as SentryRaw from '@sentry/react';

// Debug logging - only enabled in development
const DEBUG = import.meta.env.DEV;

// ============================================================================
// DBC CONFIG KEY
// ============================================================================

/**
 * dextoro Mainnet DBC Config Key — V4 (deployed 2026-04-27)
 *
 * Used for ALL new DBC pools created via dextoro. Immutable fee settings:
 *
 * - feeClaimer: dextoro treasury wallet (receives partner fees)
 * - cliffFeeNumerator: 200bps total BC trading fee (unchanged from V4 redeploy)
 * - creatorTradingFeePercentage: 50 (creator gets 50% of pool share)
 * - migrationFee.creatorFeePercentage: 0 (100% to treasury, unchanged)
 * - migrationFee.feePercentage: 50 (V4 was 10)
 * - 8 SOL initial / 40 SOL migration FDV (V4 was 30 / 400)
 *
 * Effective rates after Meteora's 20% off-the-top:
 *   0.80% creator / 0.80% dextoro / 0.40% Meteora = 2.00% total
 *
 * DO NOT create ad-hoc configs per token. All launches must use this config
 * to ensure consistent fee routing to the dextoro treasury.
 */
export const DEXTORO_DBC_CONFIG_KEY = new PublicKey('CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao');

/**
 * Alias for the active dextoro DBC config key (currently V5).
 */
export const MEMELAUNCH_DBC_CONFIG_KEY = DEXTORO_DBC_CONFIG_KEY;

/**
 * Retired V3 config (active until 2026-04-27).
 *
 * V3 had 100bps total trading fee (1%). Pools and coins launched before V4
 * deploy still reference this config on-chain. Use this constant only for
 * historical lookups against pre-V4 pools by name (e.g. archival queries,
 * one-off ops scripts). Trading lookups should rely on the historical array
 * below, which falls back through retired configs automatically.
 */
export const DEXTORO_DBC_CONFIG_KEY_V3_LEGACY = new PublicKey('2rQyPnj2VjWLZTEXve8tazSaQTsCNEfyzQHEjXXk3aZp');

/**
 * Historical DBC config keys, ordered newest-first.
 *
 * Trading reads (quote/swap/pool lookup) iterate this list to find the pool
 * for a given mint, since older coins were created against retired configs
 * with different fee/curve parameters. New launches always use
 * DEXTORO_DBC_CONFIG_KEY (the head of this list).
 *
 * To retire a config: prepend the new key to this array and update
 * DEXTORO_DBC_CONFIG_KEY to match. Trading for older coins keeps working
 * because the lookup falls back through the rest of the array.
 */
export const DEXTORO_DBC_CONFIG_KEYS_HISTORICAL: PublicKey[] = [
  new PublicKey('CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao'), // v5 active 2026-05-06 (200bps, 8→40 SOL FDV, 50/50 splits, 50% migration fee)
  new PublicKey('29oQKJkrj8dFqiaqf1ekWytYTcLUWqLAx4oFYRnkhXss'), // v7-stealth one-off 2026-05-10 (Dextoro coin only, 377.45 SOL threshold, 30% supply on migration, $100K migration MC, 5% post-grad)
  new PublicKey('5YAPBAiKiDnkpLpnjLK6XCpRKRoQqZxNex5mczcqCE1E'), // v4 previous default 2026-05-02 to 2026-05-06 (200bps, 30→400 SOL FDV)
  new PublicKey('DiCQ5vCxghiqyTNNLZrGGpBD52taNi5FxW9uSKErkW4P'), // v4 MISDEPLOY 2026-04-27 to 2026-05-02, on-chain rate 1%, pools still trade here
  new PublicKey('2rQyPnj2VjWLZTEXve8tazSaQTsCNEfyzQHEjXXk3aZp'), // v3 retired 2026-04-27 (100bps)
  new PublicKey('2AxDw9a1PbWJQHeAANkw1WmtWL88GMfJCtrdvQhcajXz'), // v2 retired (MECHA + legacy, 56.36 SOL threshold)
];

// Native SOL mint (wrapped SOL)
const NATIVE_SOL_MINT = new PublicKey('So11111111111111111111111111111111111111112');

// Token decimals are fetched from the on-chain config
// These constants are fallbacks only - actual values come from ensureConfigTokenDecimal()
const BASE_TOKEN_DECIMALS = 6;
const BASE_TOKEN_MULTIPLIER = 10 ** BASE_TOKEN_DECIMALS; // 1_000_000 (fallback)

/**
 * Safely convert a value to a number, returning null if unsafe or invalid.
 * Handles BigInt, BN, and objects with .toNumber() method.
 */
/**
 * Safely convert a value to a number, returning null if unsafe or invalid.
 * Handles BigInt, BN, and objects with .toNumber() method.
 * This function NEVER throws - it always returns a number or null.
 */
function safeNumber(value: unknown): number | null {
  try {
    if (value === null || value === undefined) {
      return null;
    }

    // Already a number
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return null;
      return value;
    }

    // BigInt
    if (typeof value === 'bigint') {
      if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
        console.warn('[safeNumber] BigInt exceeds safe integer range');
        return null;
      }
      return Number(value);
    }

    // Decimal.js object (has toNumber and isFinite methods)
    if (typeof value === 'object' && value !== null && 'toNumber' in value) {
      try {
        const num = (value as { toNumber: () => number }).toNumber();

        // Check if the result is valid
        if (!Number.isFinite(num)) {
          console.warn('[safeNumber] toNumber() returned non-finite value');
          return null;
        }

        // For BN/BigInt-like objects, check safe integer range
        // For Decimal objects (prices), allow any finite number
        const isDecimalLike = 'isFinite' in value || 'decimalPlaces' in value;
        if (!isDecimalLike && (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER)) {
          console.warn('[safeNumber] BN value exceeds safe integer range');
          return null;
        }

        return num;
      } catch (e) {
        console.warn('[safeNumber] toNumber() threw:', e instanceof Error ? e.message : 'unknown');
        return null;
      }
    }

    // String that might be a number
    if (typeof value === 'string') {
      // Check for very long numeric strings
      if (value.length > 16 && /^\d+$/.test(value)) {
        console.warn('[safeNumber] Numeric string too long');
        return null;
      }
      const parsed = parseFloat(value);
      if (!Number.isFinite(parsed)) return null;
      if (parsed > Number.MAX_SAFE_INTEGER || parsed < Number.MIN_SAFE_INTEGER) {
        return null;
      }
      return parsed;
    }

    return null;
  } catch {
    // Absolute fallback - never throw
    return null;
  }
}

/**
 * Convert a raw on-chain integer amount (BN, bigint, string, number) into a
 * float scaled by `decimals`. Unlike safeNumber+division, this keeps the raw
 * magnitude in string domain until after the scale-down, so values larger
 * than 2^53 (e.g. v7-stealth virtual base reserves) don't trip the BN.toNumber
 * safe-integer guard. Returns null only if the input isn't a valid integer.
 */
export function rawAmountToScaled(
  value: { toString(): string } | string | number | bigint | null | undefined,
  decimals: number,
): number | null {
  if (value === null || value === undefined) return null;
  try {
    const str = typeof value === 'string' ? value : String(value);
    if (!/^-?\d+$/.test(str)) return null;
    if (decimals <= 0) {
      const n = Number(str);
      return Number.isFinite(n) ? n : null;
    }
    const negative = str.startsWith('-');
    const digits = negative ? str.slice(1) : str;
    const padded = digits.padStart(decimals + 1, '0');
    const intPart = padded.slice(0, -decimals);
    const fracPart = padded.slice(-decimals);
    const n = parseFloat(`${negative ? '-' : ''}${intPart}.${fracPart}`);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Interface for validated config details including fee information
 */
interface ValidatedConfigDetails {
  quoteMint: string;
  tokenDecimal: number;
  tokenType: number;
  fixedTokenSupplyFlag: number;
  migrationOption: number;
  collectFeeMode: number;
  creatorTradingFeePercentage: number;
  partnerLockedLpPercentage: number;
  creatorLockedLpPercentage: number;
  // Fee-related fields
  feeClaimer?: string;
  migrationFeePercentage?: number;
  creatorMigrationFeePercentage?: number;
  tradingFee?: {
    cliffFeeNumerator: string;
    baseFeeMode: number;
  };
  // Validation against expected MemeLaunch settings
  feeConfigValidation?: ConfigValidationResult;
  isValid: boolean;
  validationErrors: string[];
}

/**
 * Meteora Dynamic Bonding Curve Client
 * 
 * This module encapsulates all interactions with Meteora's DBC protocol.
 * Configured for Solana mainnet with Dextoro config key.
 * 
 * CONFIGURATION:
 * - Quote mint: WSOL (So11111111111111111111111111111111111111112)
 * - Config key: CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao (V5 active, 2026-05-06)
 * - Decimals: base=6 (from config), quote=9 (SOL)
 * - Treasury: 9ynRciWU7m3vGtGmm6s9YRW2avXA5D64AMbcedhakxQL
 * 
 * FEE STRUCTURE:
 * - Protocol fees: Defined in the on-chain config (cannot be changed from frontend)
 * - Referral fees: Routed to treasury WSOL ATA via referralTokenAccount
 * - Creator fees: TODO - will route to token creator's wallet
 * 
 * IMPORTANT: This uses the Meteora DBC "quick launch" flow where:
 * - The SDK creates the token mint (we do NOT pre-mint)
 * - The SDK creates the Metaplex metadata
 * - The SDK initializes the bonding curve pool
 * - All in a single transaction
 */
export class MeteoraDBCClient {
  private connection: Connection;
  private dbcClient: DynamicBondingCurveClient;
  private treasuryWsolAta: PublicKey | null = null;
  // Cache the active config's tokenDecimal — used only by active-config flows
  // (new-coin creation, initial buy estimation, validation). For pool-info /
  // graduation-MC lookups against retired or one-off configs, ALWAYS use
  // tokenDecimalByConfig instead. v5 uses 6 decimals; v7-stealth uses 9.
  private configTokenDecimal: number | null = null;
  // Cache the full pool config for bonding curve estimation (active config only;
  // used for new-coin creation + initial buy estimation).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private cachedPoolConfig: any | null = null;
  // Per-config caches keyed by config base58. Different DBC configs have
  // different tokenDecimal (v5=6, v7-stealth=9) and different migrationSqrtPrice
  // (each config has its own FDV target). Lookups for retired / one-off / non-
  // active-config pools MUST use these maps; falling back to the active config's
  // singleton produces silent off-by-decimal bugs (see 2026-05-10 v7-stealth
  // $109-instead-of-$108K incident).
  private tokenDecimalByConfig = new Map<string, number>();
  private migrationSqrtPriceByConfig = new Map<string, BN>();

  // Pool state cache: mint address -> { poolAddress, poolState, config, timestamp }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private poolCache = new Map<string, { poolAddress: PublicKey; poolState: any; config: any; timestamp: number }>();
  // Per-mint resolved DBC config cache. Once we know which historical config
  // a mint was created against, we don't re-iterate the array on every lookup.
  // Configs are immutable on-chain, so this never needs invalidation.
  // Static so all MeteoraDBCClient instances on the page share one cache:
  // useMeteoraClient, useBatchTokenPrices, useTokenStatsQuery, etc. each
  // construct their own client and would otherwise each cold-iterate the
  // historical configs the first time a mint shows up.
  private static mintToConfigCache = new Map<string, PublicKey>();
  private poolRefreshInterval: ReturnType<typeof setTimeout> | null = null;
  private poolRefreshMint: string | null = null;
  private poolRefreshFailures = 0;
  private static POOL_CACHE_TTL_MS = 3000;
  private static POOL_REFRESH_MS = 2000;

  // Blockhash cache
  private blockhashCache: { blockhash: string; lastValidBlockHeight: number; fetchedAt: number } | null = null;
  private blockhashRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private static BLOCKHASH_MAX_AGE_MS = 30000;
  private static BLOCKHASH_REFRESH_MS = 20000;

  constructor(endpoint: string) {
    // Configure connection with explicit WS endpoint to avoid deriving from HTTP
    const wsEndpoint = getSolanaWsEndpoint();
    this.connection = new Connection(endpoint, {
      commitment: 'confirmed',
      confirmTransactionInitialTimeout: 60000,
      wsEndpoint: wsEndpoint,
    });
    this.dbcClient = new DynamicBondingCurveClient(this.connection, 'confirmed');
    DEBUG && console.log('[MeteoraDBCClient] ========================================');
    DEBUG && console.log('[MeteoraDBCClient] INITIALIZATION');
    DEBUG && console.log('[MeteoraDBCClient] ========================================');
    DEBUG && console.log('[MeteoraDBCClient] HTTP Endpoint:', endpoint);
    DEBUG && console.log('[MeteoraDBCClient] WS Endpoint:', wsEndpoint);
    DEBUG && console.log('[MeteoraDBCClient] Config Key:', DEXTORO_DBC_CONFIG_KEY.toBase58());
    DEBUG && console.log('[MeteoraDBCClient] Quote Mint (WSOL):', NATIVE_SOL_MINT.toBase58());
    DEBUG && console.log('[MeteoraDBCClient] Treasury Wallet:', DEXTORO_TREASURY_WALLET.toBase58());
    
    // Pre-compute treasury WSOL ATA
    this.initTreasuryAta();
  }

  /**
   * Initialize treasury WSOL ATA for referral fees
   */
  private async initTreasuryAta(): Promise<void> {
    try {
      this.treasuryWsolAta = await getTreasuryWsolAta();
      DEBUG && console.log('[MeteoraDBCClient] Treasury WSOL ATA:', this.treasuryWsolAta.toBase58());
    } catch (error) {
      console.error('[MeteoraDBCClient] Failed to derive treasury ATA:', error);
    }
  }

  // ============================================================================
  // Pool State Cache
  // ============================================================================

  /**
   * Get cached pool state for a mint address.
   * Returns cached data if < 3s old. If stale but present, returns stale data
   * and triggers background refresh. If missing, fetches synchronously.
   */
  async getCachedPoolState(mintAddress: string): Promise<{ poolAddress: PublicKey; poolState: any; config: any } | null> {
    const cached = this.poolCache.get(mintAddress);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < MeteoraDBCClient.POOL_CACHE_TTL_MS) {
      DEBUG && console.log('[MeteoraDBCClient] Pool cache hit:', mintAddress.slice(0, 8));
      return cached;
    }

    if (cached) {
      // Stale but present: return stale data, refresh in background
      DEBUG && console.log('[MeteoraDBCClient] Pool cache stale, refreshing:', mintAddress.slice(0, 8));
      this.warmPoolCache(mintAddress).catch(() => {});
      return cached;
    }

    // Cache miss: fetch synchronously
    DEBUG && console.log('[MeteoraDBCClient] Pool cache miss:', mintAddress.slice(0, 8));
    await this.warmPoolCache(mintAddress);
    return this.poolCache.get(mintAddress) ?? null;
  }

  /**
   * Pre-fetch pool state + config and store in cache.
   * Called on token page load and periodically during refresh.
   */
  async warmPoolCache(mintAddress: string): Promise<void> {
    try {
      const mintPubkey = new PublicKey(mintAddress);
      const poolLookup = await this.getPoolForMint(mintPubkey);

      if (!poolLookup.exists || !poolLookup.poolAddress || !poolLookup.poolState) {
        DEBUG && console.log('[MeteoraDBCClient] Pool cache warm: pool not found for', mintAddress.slice(0, 8));
        return;
      }

      const config = await this.dbcClient.state.getPoolConfig(poolLookup.poolState.config);

      this.poolCache.set(mintAddress, {
        poolAddress: poolLookup.poolAddress,
        poolState: poolLookup.poolState,
        config,
        timestamp: Date.now(),
      });

      // Reset backoff on success
      if (this.poolRefreshFailures > 0) {
        console.log('[MeteoraDBCClient] Pool refresh restored to normal interval');
      }
      this.poolRefreshFailures = 0;

      DEBUG && console.log('[MeteoraDBCClient] Pool cache warmed:', mintAddress.slice(0, 8));
    } catch (error) {
      this.poolRefreshFailures++;
      if (this.poolRefreshFailures === 1) {
        console.warn('[MeteoraDBCClient] Pool refresh backing off due to upstream failure', error);
      }
    }
  }

  /**
   * Start background pool state refresh. Refreshes every 2s.
   * If already refreshing a different mint, clears old interval.
   */
  private getPoolRefreshDelay(): number {
    if (this.poolRefreshFailures === 0) return MeteoraDBCClient.POOL_REFRESH_MS;
    return Math.min(MeteoraDBCClient.POOL_REFRESH_MS * Math.pow(2, this.poolRefreshFailures), 30000);
  }

  private reschedulePoolRefresh(mintAddress: string): void {
    if (this.poolRefreshInterval) {
      clearTimeout(this.poolRefreshInterval);
      this.poolRefreshInterval = null;
    }
    const delay = this.getPoolRefreshDelay();
    this.poolRefreshInterval = setTimeout(async () => {
      await this.warmPoolCache(mintAddress).catch(() => {});
      // Schedule next tick if still refreshing this mint
      if (this.poolRefreshMint === mintAddress) {
        this.reschedulePoolRefresh(mintAddress);
      }
    }, delay);
  }

  startPoolRefresh(mintAddress: string): void {
    if (this.poolRefreshMint === mintAddress && this.poolRefreshInterval) {
      return; // Already refreshing this mint
    }
    this.stopPoolRefresh();
    this.poolRefreshMint = mintAddress;
    this.poolRefreshFailures = 0;
    this.reschedulePoolRefresh(mintAddress);
    DEBUG && console.log('[MeteoraDBCClient] Pool refresh started for:', mintAddress.slice(0, 8));
  }

  /**
   * Stop background pool state refresh.
   */
  stopPoolRefresh(): void {
    if (this.poolRefreshInterval) {
      clearTimeout(this.poolRefreshInterval);
      this.poolRefreshInterval = null;
      this.poolRefreshMint = null;
      DEBUG && console.log('[MeteoraDBCClient] Pool refresh stopped');
    }
  }

  // ============================================================================
  // Blockhash Cache
  // ============================================================================

  /**
   * Get cached blockhash if < 30s old. Otherwise fetches fresh.
   */
  async getCachedBlockhash(): Promise<{ blockhash: string; lastValidBlockHeight: number }> {
    if (this.blockhashCache && (Date.now() - this.blockhashCache.fetchedAt) < MeteoraDBCClient.BLOCKHASH_MAX_AGE_MS) {
      DEBUG && console.log('[MeteoraDBCClient] Blockhash cache hit');
      return { blockhash: this.blockhashCache.blockhash, lastValidBlockHeight: this.blockhashCache.lastValidBlockHeight };
    }
    DEBUG && console.log('[MeteoraDBCClient] Blockhash cache miss, fetching fresh');
    await this.refreshBlockhash();
    return { blockhash: this.blockhashCache!.blockhash, lastValidBlockHeight: this.blockhashCache!.lastValidBlockHeight };
  }

  /**
   * Fetch and cache the latest blockhash.
   */
  async refreshBlockhash(): Promise<void> {
    try {
      const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed');
      this.blockhashCache = { blockhash, lastValidBlockHeight, fetchedAt: Date.now() };
    } catch (error) {
      console.warn('[MeteoraDBCClient] Blockhash refresh failed:', error);
    }
  }

  /**
   * Start background blockhash refresh every 20s. Also fetches immediately.
   */
  startBlockhashRefresh(): void {
    if (this.blockhashRefreshInterval) return;
    this.refreshBlockhash().catch(() => {});
    this.blockhashRefreshInterval = setInterval(() => {
      this.refreshBlockhash().catch(() => {});
    }, MeteoraDBCClient.BLOCKHASH_REFRESH_MS);
    DEBUG && console.log('[MeteoraDBCClient] Blockhash refresh started');
  }

  /**
   * Stop background blockhash refresh.
   */
  stopBlockhashRefresh(): void {
    if (this.blockhashRefreshInterval) {
      clearInterval(this.blockhashRefreshInterval);
      this.blockhashRefreshInterval = null;
      DEBUG && console.log('[MeteoraDBCClient] Blockhash refresh stopped');
    }
  }

  // ============================================================================

  /**
   * Fetch and cache the full pool config from on-chain.
   * Used for both tokenDecimal and bonding curve estimation.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async ensurePoolConfig(): Promise<any> {
    if (this.cachedPoolConfig !== null) {
      return this.cachedPoolConfig;
    }

    try {
      DEBUG && console.log('[Meteora DBC] Fetching pool config from on-chain...');
      const poolConfig = await this.dbcClient.state.getPoolConfig(DEXTORO_DBC_CONFIG_KEY);

      if (poolConfig) {
        this.cachedPoolConfig = poolConfig;
        const activeKey = DEXTORO_DBC_CONFIG_KEY.toBase58();
        if (typeof poolConfig.tokenDecimal === 'number') {
          this.configTokenDecimal = poolConfig.tokenDecimal;
          this.tokenDecimalByConfig.set(activeKey, poolConfig.tokenDecimal);
        }
        if (poolConfig.migrationSqrtPrice) {
          this.migrationSqrtPriceByConfig.set(
            activeKey,
            new BN(poolConfig.migrationSqrtPrice.toString()),
          );
        }
        DEBUG && console.log('[Meteora DBC] Cached pool config, tokenDecimal:', this.configTokenDecimal);
        return this.cachedPoolConfig;
      }
    } catch (error) {
      DEBUG && console.warn('[Meteora DBC] Failed to fetch pool config:', error);
    }

    return null;
  }

  /**
   * Ensure the ACTIVE config's tokenDecimal is set. ONLY for active-config-only
   * flows (new-coin creation, validation). Pool-info / graduation-MC lookups
   * must use getTokenDecimalForConfig with the resolved pool's config key.
   */
  private async ensureConfigTokenDecimal(): Promise<number> {
    if (this.configTokenDecimal !== null) {
      return this.configTokenDecimal;
    }

    await this.ensurePoolConfig();

    if (this.configTokenDecimal !== null) {
      return this.configTokenDecimal;
    }

    // Fallback to 6 (what our active config was created with)
    DEBUG && console.log('[Meteora DBC] Using fallback tokenDecimal: 6');
    this.configTokenDecimal = 6;
    return 6;
  }

  /**
   * Fetch (and cache) both tokenDecimal AND migrationSqrtPrice for a specific
   * DBC config in a single RPC round trip. Required for any code path that
   * operates on pools created against retired or one-off configs (v7-stealth,
   * future rotations). NEVER substitute the active config's values — different
   * configs have different decimals (v5=6, v7-stealth=9) and FDV geometry.
   */
  private async fetchConfigStateForConfig(configBase58: string): Promise<void> {
    const hasDecimal = this.tokenDecimalByConfig.has(configBase58);
    const hasSqrt = this.migrationSqrtPriceByConfig.has(configBase58);
    if (hasDecimal && hasSqrt) return;
    try {
      const cfg = await this.dbcClient.state.getPoolConfig(new PublicKey(configBase58));
      if (cfg) {
        if (typeof cfg.tokenDecimal === 'number') {
          this.tokenDecimalByConfig.set(configBase58, cfg.tokenDecimal);
        }
        if (cfg.migrationSqrtPrice) {
          this.migrationSqrtPriceByConfig.set(
            configBase58,
            new BN(cfg.migrationSqrtPrice.toString()),
          );
        }
      }
    } catch (err) {
      DEBUG && console.warn('[Meteora DBC] fetchConfigStateForConfig failed', configBase58, err);
    }
  }

  /**
   * Resolve the per-config tokenDecimal. Returns null if the config can't be
   * fetched (caller must decide whether to fail loudly or fall back). NEVER
   * silently substitute the active config's decimal — see header comment on
   * tokenDecimalByConfig.
   */
  private async getTokenDecimalForConfig(configBase58: string): Promise<number | null> {
    const cached = this.tokenDecimalByConfig.get(configBase58);
    if (cached !== undefined) return cached;
    await this.fetchConfigStateForConfig(configBase58);
    const fetched = this.tokenDecimalByConfig.get(configBase58);
    return fetched !== undefined ? fetched : null;
  }

  /**
   * Resolve the per-config migrationSqrtPrice (raw BN). Returns null if the
   * config can't be fetched. Each DBC config has its own migration target;
   * substituting the active config's sqrtPrice silently displays the wrong
   * graduation FDV for non-active-config pools.
   */
  private async getMigrationSqrtPriceForConfig(configBase58: string): Promise<BN | null> {
    const cached = this.migrationSqrtPriceByConfig.get(configBase58);
    if (cached) return cached;
    await this.fetchConfigStateForConfig(configBase58);
    return this.migrationSqrtPriceByConfig.get(configBase58) ?? null;
  }

  /**
   * Compute the graduation market cap (FDV at migration) in USD for a specific
   * DBC config. The config arg is REQUIRED — pools created against retired or
   * one-off configs have their own migrationSqrtPrice + tokenDecimal geometry,
   * and using active-config values silently displays the wrong number for them
   * (see 2026-05-10 v7-stealth $109 incident: active tokenDecimal=6 was
   * applied to v7's 9-decimal sqrt price, yielding 1/1000 of the correct MC).
   *
   * Formula (verified against SDK source):
   *   tokenPrice_natural = (sqrtPrice² / 2^128) × 10^(baseDecimal - quoteDecimal)
   *   graduationMC_USD   = tokenPrice_natural × totalSupply(1B) × solPriceUSD
   *
   * Includes a range-check assertion against MIGRATION_FDV_SOL_BY_CONFIG: a
   * computed value outside 50%-200% of `expected_fdv_sol × solPriceUSD` emits
   * a loud console.warn with the inputs for next-time debugging.
   */
  async getGraduationMarketCapUSD(
    solPriceUSD: number,
    configBase58: string,
  ): Promise<number | null> {
    const migrationSqrt = await this.getMigrationSqrtPriceForConfig(configBase58);
    if (!migrationSqrt) return null;

    const baseDecimal = await this.getTokenDecimalForConfig(configBase58);
    if (baseDecimal === null) {
      console.warn('[Meteora DBC] getGraduationMarketCapUSD: tokenDecimal unresolved for config', configBase58);
      return null;
    }

    const baseTokenDecimal =
      baseDecimal === 6 ? TokenDecimal.SIX :
      baseDecimal === 7 ? TokenDecimal.SEVEN :
      baseDecimal === 8 ? TokenDecimal.EIGHT : TokenDecimal.NINE;

    const graduationPrice = getPriceFromSqrtPrice(
      migrationSqrt,
      baseTokenDecimal,
      TokenDecimal.NINE,
    );
    const graduationPriceSOL = safeNumber(graduationPrice);
    if (graduationPriceSOL === null || graduationPriceSOL <= 0) return null;

    const computed = graduationPriceSOL * 1_000_000_000 * solPriceUSD;

    // Range-check: catch decimal/unit mismatches loudly so the next bug of
    // this shape shows up in console + Sentry immediately instead of as a
    // wrong-by-1000x user-facing number. After the 2026-05-10 patch chain
    // this case should be impossible; that's exactly why we want a Sentry
    // event the moment it fires (console.warn alone gets lost in prod noise).
    const expectedFDV_SOL = MIGRATION_FDV_SOL_BY_CONFIG[configBase58];
    if (expectedFDV_SOL !== undefined && solPriceUSD > 0) {
      const expectedUSD = expectedFDV_SOL * solPriceUSD;
      const ratio = computed / expectedUSD;
      if (ratio < 0.5 || ratio > 2.0) {
        const skew = ratio < 1 ? Math.round(1 / ratio) : Math.round(ratio);
        const message = `[Meteora DBC] Graduation MC sanity-check FAILED for config ${configBase58}: computed=$${computed.toFixed(2)} expected≈$${expectedUSD.toFixed(2)} ratio=${ratio.toFixed(4)} (off by ${skew}x). Indicates a decimal/unit mismatch or wrong migrationSqrtPrice readback.`;
        const context = {
          configBase58,
          migrationSqrt: migrationSqrt.toString(),
          baseDecimal,
          graduationPriceSOL,
          solPriceUSD,
          computed,
          expectedUSD,
          ratio,
          skew,
          expectedFDV_SOL,
        };
        console.warn(message, context);
        try {
          SentryRaw.captureMessage(message, {
            level: 'warning',
            tags: {
              environment: import.meta.env.VITE_APP_ENV ?? 'unknown',
              feature: 'bonding-curve-mc',
              config_key: configBase58,
            },
            extra: context,
          });
        } catch {
          // Sentry not initialized (e.g. dev mode); console.warn above is the floor.
        }
      }
    }

    return computed;
  }

  /**
   * Estimate token output for an initial buy using the bonding curve math.
   * Uses calculateQuoteToBaseFromAmountIn from the Meteora SDK to walk the
   * config's curve segments — no live pool required.
   *
   * @param solAmount Amount of SOL to spend
   * @returns { tokens, percentage } or null on failure
   */
  async estimateInitialBuyTokens(solAmount: number): Promise<{ tokens: number; percentage: number } | null> {
    try {
      const poolConfig = await this.ensurePoolConfig();
      if (!poolConfig) {
        console.warn('[Meteora DBC] Cannot estimate: pool config not available');
        return null;
      }

      const { sqrtStartPrice, curve } = poolConfig;
      if (!sqrtStartPrice || !curve || !Array.isArray(curve)) {
        console.warn('[Meteora DBC] Cannot estimate: missing sqrtStartPrice or curve');
        return null;
      }

      // Find the last non-zero curve segment's sqrtPrice as stopSqrtPrice
      let stopSqrtPrice: typeof sqrtStartPrice | null = null;
      for (let i = curve.length - 1; i >= 0; i--) {
        if (curve[i].sqrtPrice && !curve[i].sqrtPrice.isZero()) {
          stopSqrtPrice = curve[i].sqrtPrice;
          break;
        }
      }

      if (!stopSqrtPrice) {
        console.warn('[Meteora DBC] Cannot estimate: no non-zero curve segment found');
        return null;
      }

      // Convert SOL to lamports as BN
      const amountInLamports = new BN(Math.floor(solAmount * LAMPORTS_PER_SOL));

      const result = calculateQuoteToBaseFromAmountIn(
        { curve },
        sqrtStartPrice,
        amountInLamports,
        stopSqrtPrice,
      );

      // Convert output from smallest units (token decimals) to human-readable
      const tokenDecimals = this.configTokenDecimal ?? BASE_TOKEN_DECIMALS;
      const divisor = 10 ** tokenDecimals;
      const outputNum = safeNumber(result.outputAmount);
      if (outputNum === null || outputNum <= 0) {
        return null;
      }

      const tokens = outputNum / divisor;
      const TOKEN_SUPPLY = 1_000_000_000;
      const percentage = (tokens / TOKEN_SUPPLY) * 100;

      DEBUG && console.log('[Meteora DBC] Estimate:', {
        solAmount,
        lamports: amountInLamports.toString(),
        rawOutput: result.outputAmount.toString(),
        tokens,
        percentage: percentage.toFixed(4) + '%',
      });

      return { tokens, percentage };
    } catch (error) {
      console.warn('[Meteora DBC] estimateInitialBuyTokens failed:', error);
      return null;
    }
  }

  /**
   * Get the treasury WSOL ATA (lazy init if needed)
   */
  private async getTreasuryAta(): Promise<PublicKey | null> {
    if (!this.treasuryWsolAta) {
      await this.initTreasuryAta();
    }
    return this.treasuryWsolAta;
  }

  /**
   * Validate the DBC config key on-chain before pool creation
   * This ensures our parameters match what the config expects.
   * Also logs detailed fee configuration for debugging.
   */
  async validateConfigKey(): Promise<ValidatedConfigDetails> {
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] VALIDATING CONFIG KEY');
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Config Key:', DEXTORO_DBC_CONFIG_KEY.toBase58());
    
    const validationErrors: string[] = [];
    let configDetails: ValidatedConfigDetails = {
      quoteMint: '',
      tokenDecimal: 0,
      tokenType: 0,
      fixedTokenSupplyFlag: 0,
      migrationOption: 0,
      collectFeeMode: 0,
      creatorTradingFeePercentage: 0,
      partnerLockedLpPercentage: 0,
      creatorLockedLpPercentage: 0,
      isValid: false,
      validationErrors: [],
    };

    try {
      const poolConfig = await this.dbcClient.state.getPoolConfig(DEXTORO_DBC_CONFIG_KEY);
      
      if (!poolConfig) {
        validationErrors.push(`Config key ${DEXTORO_DBC_CONFIG_KEY.toBase58()} not found on-chain`);
        configDetails.validationErrors = validationErrors;
        return configDetails;
      }

      // Extract fee-related details from the config
      // Note: Access raw data since types may not include all fields
      const rawConfig = poolConfig as Record<string, unknown>;
      const baseFeeNumerator = (rawConfig.baseFee as Record<string, unknown>)?.cliffFeeNumerator?.toString() || 
                               rawConfig.cliffFeeNumerator?.toString() || '0';
      const baseFeeMode = (rawConfig.baseFee as Record<string, unknown>)?.baseFeeMode ?? 
                          rawConfig.baseFeeMode ?? 0;
      
      // Extract feeClaimer - this is the partner wallet that receives fees
      const feeClaimerPubkey = (rawConfig.feeClaimer as PublicKey) || 
                               (rawConfig.feeClaimerPublicKey as PublicKey);
      const feeClaimerStr = feeClaimerPubkey?.toBase58?.() || undefined;
      
      // Extract migration fee details
      const migrationFeePercentage = rawConfig.migrationFeePercentage as number | undefined;
      const creatorMigrationFeePercentage = rawConfig.creatorMigrationFeePercentage as number | undefined;
      
      // Extract config details with fee info
      configDetails = {
        quoteMint: poolConfig.quoteMint.toBase58(),
        tokenDecimal: poolConfig.tokenDecimal,
        tokenType: poolConfig.tokenType,
        fixedTokenSupplyFlag: poolConfig.fixedTokenSupplyFlag,
        migrationOption: poolConfig.migrationOption,
        collectFeeMode: poolConfig.collectFeeMode,
        creatorTradingFeePercentage: poolConfig.creatorTradingFeePercentage,
        partnerLockedLpPercentage: poolConfig.partnerLockedLpPercentage,
        creatorLockedLpPercentage: poolConfig.creatorLockedLpPercentage,
        feeClaimer: feeClaimerStr,
        migrationFeePercentage,
        creatorMigrationFeePercentage,
        tradingFee: {
          cliffFeeNumerator: String(baseFeeNumerator),
          baseFeeMode: Number(baseFeeMode),
        },
        isValid: true,
        validationErrors: [],
      };

      // Cache the tokenDecimal for price calculations
      this.configTokenDecimal = poolConfig.tokenDecimal;
      DEBUG && console.log('[Meteora DBC] Cached tokenDecimal:', this.configTokenDecimal);

      // Validate fee configuration against expected MemeLaunch settings
      const feeValidation = validateFeeConfig({
        feeClaimer: feeClaimerPubkey,
        creatorTradingFeePercentage: poolConfig.creatorTradingFeePercentage,
        migrationFeePercentage,
        creatorMigrationFeePercentage,
      });
      configDetails.feeConfigValidation = feeValidation;

      // Log detailed config for debugging
      DEBUG && console.log('[Meteora DBC] ----------------------------------------');
      DEBUG && console.log('[Meteora DBC] CONFIG DETAILS:');
      DEBUG && console.log('[Meteora DBC] Quote Mint:', configDetails.quoteMint);
      DEBUG && console.log('[Meteora DBC] Token Decimal:', configDetails.tokenDecimal);
      DEBUG && console.log('[Meteora DBC] Token Type:', poolConfig.tokenType, '(0=SPL, 1=Token2022)');
      DEBUG && console.log('[Meteora DBC] Collect Fee Mode:', poolConfig.collectFeeMode);
      DEBUG && console.log('[Meteora DBC] ----------------------------------------');
      DEBUG && console.log('[Meteora DBC] FEE CONFIGURATION:');
      DEBUG && console.log('[Meteora DBC] Fee Claimer (Partner):', feeClaimerStr || 'Not set');
      DEBUG && console.log('[Meteora DBC] Creator Trading Fee %:', poolConfig.creatorTradingFeePercentage, `(Partner gets ${100 - poolConfig.creatorTradingFeePercentage}%)`);
      DEBUG && console.log('[Meteora DBC] Migration Fee %:', migrationFeePercentage ?? 'Not set');
      DEBUG && console.log('[Meteora DBC] Creator Migration Fee %:', creatorMigrationFeePercentage ?? 'Not set');
      DEBUG && console.log('[Meteora DBC] Base Fee Numerator:', baseFeeNumerator);
      DEBUG && console.log('[Meteora DBC] Base Fee Mode:', baseFeeMode, '(0=LinearScheduler, 1=ExponentialScheduler, 2=RateLimiter)');
      DEBUG && console.log('[Meteora DBC] ----------------------------------------');
      DEBUG && console.log('[Meteora DBC] EXPECTED MEMELAUNCH SETTINGS:');
      DEBUG && console.log('[Meteora DBC] Expected Treasury:', MEMELAUNCH_TREASURY_PUBKEY.toBase58());
      DEBUG && console.log('[Meteora DBC] Expected Creator Trading Fee %:', CREATOR_TRADING_FEE_PERCENTAGE);
      DEBUG && console.log('[Meteora DBC] Expected Partner Trading Fee %:', PARTNER_TRADING_FEE_PERCENTAGE);
      DEBUG && console.log('[Meteora DBC] ----------------------------------------');
      DEBUG && console.log('[Meteora DBC] FEE CONFIG VALIDATION:');
      DEBUG && console.log('[Meteora DBC] Is Valid:', feeValidation.isValid);
      if (feeValidation.errors.length > 0) {
        console.error('[Meteora DBC] Errors:', feeValidation.errors);
      }
      if (feeValidation.warnings.length > 0) {
        console.warn('[Meteora DBC] Warnings:', feeValidation.warnings);
      }
      DEBUG && console.log('[Meteora DBC] ========================================');
      
      // Log MemeLaunch fee summary
      logFeeConfigSummary();

      // Validate quote mint is WSOL
      if (poolConfig.quoteMint.toBase58() !== NATIVE_SOL_MINT.toBase58()) {
        validationErrors.push(`Config quoteMint (${poolConfig.quoteMint.toBase58()}) does not match WSOL (${NATIVE_SOL_MINT.toBase58()})`);
      }

      // Note: tokenDecimal is set in the on-chain config and cannot be changed
      // This is informational - the SDK uses the on-chain value regardless
      if (poolConfig.tokenDecimal !== 9) {
        // Changed from error to warning - this is informational, not a blocker
        console.warn(`[Meteora DBC] Config tokenDecimal is ${poolConfig.tokenDecimal} (expected 9) - using on-chain value`);
      }

      // Add fee config validation errors
      if (!feeValidation.isValid) {
        validationErrors.push(...feeValidation.errors);
      }

      if (validationErrors.length > 0) {
        configDetails.isValid = false;
        configDetails.validationErrors = validationErrors;
      }

      return configDetails;
    } catch (error) {
      console.error('[Meteora DBC] Error validating config:', error);
      validationErrors.push(`Failed to fetch config: ${error instanceof Error ? error.message : 'Unknown error'}`);
      configDetails.validationErrors = validationErrors;
      return configDetails;
    }
  }

  /**
   * Create a new meme token and initialize a DBC pool using Meteora's quick-launch flow.
   * 
   * IMPORTANT: This does NOT pre-mint the token. The Meteora SDK creates:
   * - The SPL token mint
   * - Metaplex token metadata (name, symbol, uri)
   * - The bonding curve pool
   * All in a single transaction.
   * 
   * The baseMint is a new Keypair that must sign the transaction.
   * 
   * Uses HTTP-based confirmation with automatic retry on blockhash expiry.
   */
  async createMemeTokenAndPool(
    input: CreatePoolInput,
    sendTransaction: (transaction: Transaction, connection: Connection) => Promise<string>,
    onProgress?: (progress: TransactionProgress) => void
  ): Promise<CreatePoolResult> {
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Starting quick-launch token + pool creation');
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Input:', {
      name: input.name,
      symbol: input.symbol,
      creator: input.creatorWallet.toBase58(),
    });

    const payer = input.creatorWallet;

    // Step 0: Validate config key on-chain
    DEBUG && console.log('[Meteora DBC] Step 0: Validating config key...');
    const configValidation = await this.validateConfigKey();
    
    if (configValidation.validationErrors.length > 0) {
      console.error('[Meteora DBC] Config validation errors:', configValidation.validationErrors);
    }

    // Determine the mint keypair - either vanity (pre-generated) or random
    const useVanity = !!input.vanityKeypair;
    let baseMintKeypair: Keypair | null = null;
    let baseMintPublicKey: PublicKey;

    if (useVanity) {
      // Use vanity keypair - the public key is provided, signing happens server-side
      baseMintPublicKey = new PublicKey(input.vanityKeypair!.publicKey);
      DEBUG && console.log('[Meteora DBC] Using vanity keypair:', baseMintPublicKey.toBase58());
      DEBUG && console.log('[Meteora DBC] Token will have "dex" suffix!');
    } else {
      // WARNING: Random keypair - token will NOT have "dex" suffix
      // This should only happen if vanity is explicitly disabled
      console.warn('[Meteora DBC] ⚠️ USING RANDOM KEYPAIR - token will NOT have "dex" suffix!');
      console.warn('[Meteora DBC] This breaks brand consistency. Vanity should be enabled.');
      baseMintKeypair = Keypair.generate();
      baseMintPublicKey = baseMintKeypair.publicKey;
      console.warn('[Meteora DBC] Random baseMint:', baseMintPublicKey.toBase58());
    }

    // Build the metadata URI - prefer IPFS JSON metadata (Metaplex standard) over direct image URL
    const tokenUri = input.metadataUri || input.imageUrl || `https://api.dicebear.com/7.x/shapes/svg?seed=${input.symbol}`;

    // Debug parameters for error reporting
    const debugParams = {
      configKey: DEXTORO_DBC_CONFIG_KEY.toBase58(),
      baseMint: baseMintPublicKey.toBase58(),
      quoteMint: NATIVE_SOL_MINT.toBase58(),
      payer: payer.toBase58(),
      poolCreator: payer.toBase58(),
      name: input.name,
      symbol: input.symbol,
      uri: tokenUri,
      configDetails: configValidation,
      useVanity,
    };

    DEBUG && console.log('[Meteora DBC] Pool creation parameters:', JSON.stringify(debugParams, null, 2));

    let poolAddress: string | null = null;
    let poolCreated = false;
    let txSignature: string = '';
    let errorMessage: string | undefined = undefined;
    let resendCount = 0;

    try {
      // Derive the expected pool address
      const derivedPoolAddress = deriveDbcPoolAddress(
        NATIVE_SOL_MINT, // quote mint (WSOL)
        baseMintPublicKey, // base mint (our new token)
        DEXTORO_DBC_CONFIG_KEY // Dextoro mainnet config
      );
      DEBUG && console.log('[Meteora DBC] Expected pool address:', derivedPoolAddress.toBase58());

      // Step 1: Create the pool transaction using the SDK's quick-launch
      // This single call creates: mint + metadata + pool
      DEBUG && console.log('[Meteora DBC] Step 1: Creating pool transaction via SDK...');
      
      const createPoolTx = await this.dbcClient.pool.createPool({
        baseMint: baseMintPublicKey, // The SDK will create this mint (vanity or random)
        config: DEXTORO_DBC_CONFIG_KEY,
        name: input.name,
        symbol: input.symbol,
        uri: tokenUri,
        payer: payer, // Pays for transaction fees
        poolCreator: payer, // Creator of the pool (receives fees)
      });

      DEBUG && console.log('[Meteora DBC] Transaction prepared');
      DEBUG && console.log('[Meteora DBC] Instructions count:', createPoolTx.instructions.length);

      // Step 2: Send and confirm using HTTP-based polling with retry
      DEBUG && console.log('[Meteora DBC] Step 2: Sending transaction with HTTP confirmation...');
      
      const MAX_RETRIES = 3;
      const POLL_INTERVAL_MS = 1500;
      const ATTEMPT_TIMEOUT_MS = 60000;
      
      let confirmed = false;
      let lastError: string | null = null;
      
      while (!confirmed && resendCount <= MAX_RETRIES) {
        try {
          // Get fresh blockhash for each attempt
          DEBUG && console.log(`[Meteora DBC] Attempt ${resendCount + 1}/${MAX_RETRIES + 1}: Fetching fresh blockhash...`);
          const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed');
          
          createPoolTx.recentBlockhash = blockhash;
          createPoolTx.feePayer = payer;

          // Sign with baseMint keypair (required for mint creation)
          // Clear existing signatures first for retries
          createPoolTx.signatures = [];

          if (useVanity && input.vanityKeypair) {
            // VANITY FLOW: Get signature from edge function (server-side signing)
            DEBUG && console.log('[Meteora DBC] Getting vanity signature from server...');
            const txBase64 = Buffer.from(createPoolTx.serializeMessage()).toString('base64');
            const vanitySignature = await input.vanityKeypair.signTransaction(txBase64);

            // Add the vanity signature to the transaction
            // The signature needs to be at the correct index (index 0 for the mint keypair)
            const signatureBytes = decodeBase58(vanitySignature);
            createPoolTx.addSignature(baseMintPublicKey, Buffer.from(signatureBytes));
            DEBUG && console.log('[Meteora DBC] Vanity signature applied');
          } else if (baseMintKeypair) {
            // RANDOM FLOW: Sign locally with the generated keypair
            createPoolTx.partialSign(baseMintKeypair);
          }

          // Report progress
          onProgress?.({
            stage: 'sending',
            signature: null,
            blockhash,
            lastValidBlockHeight,
            currentBlockHeight: null,
            resendCount,
            lastError,
            startTime: Date.now(),
            confirmations: null,
          });

          // Send transaction via wallet adapter
          DEBUG && console.log('[Meteora DBC] Sending transaction...');
          txSignature = await sendTransaction(createPoolTx, this.connection);
          DEBUG && console.log('[Meteora DBC] Transaction sent:', txSignature);

          // Report signature
          onProgress?.({
            stage: 'confirming',
            signature: txSignature,
            blockhash,
            lastValidBlockHeight,
            currentBlockHeight: null,
            resendCount,
            lastError: null,
            startTime: Date.now(),
            confirmations: null,
          });

          // HTTP-based polling for confirmation
          const confirmResult = await this.pollForConfirmationHTTP(
            txSignature,
            lastValidBlockHeight,
            POLL_INTERVAL_MS,
            ATTEMPT_TIMEOUT_MS,
            (currentBlockHeight, confirmations) => {
              onProgress?.({
                stage: 'confirming',
                signature: txSignature,
                blockhash,
                lastValidBlockHeight,
                currentBlockHeight,
                resendCount,
                lastError: null,
                startTime: Date.now(),
                confirmations,
              });
            }
          );

          if (confirmResult.confirmed) {
            confirmed = true;
            DEBUG && console.log('[Meteora DBC] Transaction confirmed!');
            onProgress?.({
              stage: 'confirmed',
              signature: txSignature,
              blockhash,
              lastValidBlockHeight,
              currentBlockHeight: null,
              resendCount,
              lastError: null,
              startTime: Date.now(),
              confirmations: confirmResult.confirmations,
            });
          } else if (confirmResult.error) {
            // Transaction failed on-chain
            throw new Error(confirmResult.error);
          } else if (confirmResult.blockHeightExceeded) {
            // Need to retry with fresh blockhash
            lastError = 'Block height exceeded';
            DEBUG && console.log('[Meteora DBC] Block height exceeded, retrying with fresh blockhash...');
            resendCount++;
          }
        } catch (attemptError) {
          const errorMsg = attemptError instanceof Error ? attemptError.message : 'Unknown error';
          const errorLogs = (attemptError as { logs?: string[] })?.logs;
          const allErrorText = [errorMsg, ...(errorLogs || [])].join(' ');
          lastError = errorMsg;

          // Detect "already in use" — vanity keypair was previously used on-chain
          // This is NOT recoverable by retrying with the same keypair
          if (allErrorText.includes('already in use')) {
            console.error('[Meteora DBC] Mint account already exists on-chain — vanity keypair was previously used');
            // Mark the vanity keypair as used so the next attempt gets a fresh one
            if (useVanity && input.vanityKeypair) {
              try {
                await input.vanityKeypair.markAsUsed(baseMintPublicKey.toBase58(), 'already-in-use-on-chain');
                console.log('[Meteora DBC] Vanity keypair marked as used (already on-chain)');
              } catch (markErr) {
                console.warn('[Meteora DBC] Failed to mark vanity keypair as used:', markErr);
              }
            }
            throw new Error(
              'This mint address was already used on-chain. ' +
              'A previous coin creation may have succeeded without confirmation. ' +
              'Please try again. A fresh keypair will be assigned.'
            );
          }

          // Check if error is recoverable
          const isRecoverable = this.isRecoverableError(errorMsg);
          if (!isRecoverable) {
            throw attemptError;
          }

          console.warn(`[Meteora DBC] Recoverable error on attempt ${resendCount + 1}:`, errorMsg);
          resendCount++;
        }
      }

      if (!confirmed) {
        throw new Error(`Transaction failed after ${resendCount} retries: ${lastError || 'Max retries exceeded'}`);
      }

      // Step 3: Verify the pool was created
      DEBUG && console.log('[Meteora DBC] Step 3: Verifying pool creation...');
      
      // Small delay to allow indexing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const verifyPool = await this.dbcClient.state.getPoolByBaseMint(baseMintPublicKey);
      
      if (!verifyPool) {
        console.warn('[Meteora DBC] Pool not found immediately after creation - may still be indexing');
        // Don't throw - the transaction succeeded, pool should exist
        poolAddress = derivedPoolAddress.toBase58();
        poolCreated = true;
      } else {
        poolAddress = verifyPool.publicKey.toBase58();
        poolCreated = true;
        DEBUG && console.log('[Meteora DBC] Pool verified on-chain:', poolAddress);
      }

      DEBUG && console.log('[Meteora DBC] ========================================');
      DEBUG && console.log('[Meteora DBC] SUCCESS!');
      DEBUG && console.log('[Meteora DBC] ========================================');
      DEBUG && console.log('[Meteora DBC] Mint:', baseMintPublicKey.toBase58());
      DEBUG && console.log('[Meteora DBC] Pool:', poolAddress);
      DEBUG && console.log('[Meteora DBC] Tx:', txSignature);
      DEBUG && console.log('[Meteora DBC] Vanity:', useVanity ? 'YES (dex suffix)' : 'NO');

      // If using vanity keypair, mark it as used
      if (useVanity && input.vanityKeypair) {
        try {
          await input.vanityKeypair.markAsUsed(baseMintPublicKey.toBase58(), txSignature);
          DEBUG && console.log('[Meteora DBC] Vanity keypair marked as used');
        } catch (markError) {
          // Don't fail the whole operation if marking fails
          console.warn('[Meteora DBC] Failed to mark vanity keypair as used:', markError);
        }
      }

    } catch (error: unknown) {
      // ========================================
      // DETAILED ERROR HANDLING
      // ========================================
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] POOL CREATION FAILED');
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] Full error:', error);
      
      // Extract error message
      let extractedMessage = 'Unknown error';
      if (error instanceof Error) {
        extractedMessage = error.message;
        console.error('[Meteora DBC] Error message:', error.message);
      }
      
      // Check for Solana transaction logs
      let programLogs: string[] = [];
      const errorWithLogs = error as { logs?: string[] };
      if (errorWithLogs.logs && Array.isArray(errorWithLogs.logs)) {
        programLogs = errorWithLogs.logs;
        console.error('[Meteora DBC] Transaction logs:');
        errorWithLogs.logs.forEach((log, i) => console.error(`  [${i}] ${log}`));
        
        // Find the first error log
        const errorLog = errorWithLogs.logs.find(
          (log) => log.includes('Error') || log.includes('failed') || log.includes('insufficient') || log.includes('custom program error')
        );
        if (errorLog) {
          extractedMessage = errorLog;
        }
      }

      // Check for simulation error
      const errorWithSimulation = error as { simulationResponse?: { logs?: string[] } };
      if (errorWithSimulation.simulationResponse?.logs) {
        console.error('[Meteora DBC] Simulation logs:');
        errorWithSimulation.simulationResponse.logs.forEach((log, i) => console.error(`  [${i}] ${log}`));
        programLogs = [...programLogs, ...errorWithSimulation.simulationResponse.logs];
      }

      // Build comprehensive error message for UI display
      errorMessage = [
        'Meteora DBC pool creation failed.',
        '',
        `Reason: ${extractedMessage}`,
        '',
        programLogs.length > 0 ? `Program logs (last 10):\n${programLogs.slice(-10).join('\n')}` : 'No program logs available',
        '',
        `Debug parameters:\n${JSON.stringify(debugParams, null, 2)}`,
      ].join('\n');
      
      console.error('[Meteora DBC] Parameters at failure:', debugParams);

      // IMPORTANT: Never release vanity keypair if a transaction was sent!
      // The transaction might have succeeded on-chain even if we got an error.
      // Only release if we failed BEFORE sending any transaction.
      // Also never release if "already in use" — the account exists on-chain.
      const allLogText = [...programLogs, extractedMessage].join(' ');
      const isAlreadyInUse = allLogText.includes('already in use') || allLogText.includes('already used on-chain');

      if (useVanity && input.vanityKeypair && !txSignature && !isAlreadyInUse) {
        try {
          await input.vanityKeypair.releaseOnFailure();
          DEBUG && console.log('[Meteora DBC] Vanity keypair released back to pool (no tx sent)');
        } catch (releaseError) {
          // Don't fail the error handling if release fails
          console.warn('[Meteora DBC] Failed to release vanity keypair:', releaseError);
        }
      } else if (useVanity && isAlreadyInUse) {
        // Account already exists on-chain — do NOT release, it would just get re-issued
        console.warn('[Meteora DBC] Vanity keypair NOT released - account already exists on-chain (previous tx succeeded)');
      } else if (useVanity && txSignature) {
        // Transaction was sent - keypair is permanently burned even on failure
        console.warn('[Meteora DBC] Vanity keypair NOT released - transaction was sent, keypair may be in use on-chain');
      }

      // Return failure result
      // NOTE: If txSignature exists, the token MAY have been created on-chain!
      // The on-chain check in reserve-and-sign-vanity will handle this case.
      return {
        mintAddress: '', // Empty - no mint exists on-chain
        poolAddress: null,
        poolCreated: false,
        metadataCreated: false,
        txSignature: txSignature || 'No transaction sent',
        poolConfig: {
          curveType: 'linear',
          protocolFeePercent: 1,
          creatorFeePercent: CREATOR_TRADING_FEE_PERCENTAGE,
          partnerFeePercent: PARTNER_TRADING_FEE_PERCENTAGE,
          migrationFeePercent: 10,
          creatorMigrationFeePercent: CREATOR_MIGRATION_FEE_PERCENTAGE,
          partnerMigrationFeePercent: PARTNER_MIGRATION_FEE_PERCENTAGE,
        },
        errorMessage,
      };
    }

    return {
      mintAddress: baseMintPublicKey.toBase58(),
      poolAddress: poolAddress,
      poolCreated: poolCreated,
      metadataCreated: poolCreated, // Metadata is created as part of the pool creation
      txSignature: txSignature,
      poolConfig: {
        curveType: 'linear',
        protocolFeePercent: 1,
        creatorFeePercent: CREATOR_TRADING_FEE_PERCENTAGE,
        partnerFeePercent: PARTNER_TRADING_FEE_PERCENTAGE,
        migrationFeePercent: 10,
        creatorMigrationFeePercent: CREATOR_MIGRATION_FEE_PERCENTAGE,
        partnerMigrationFeePercent: PARTNER_MIGRATION_FEE_PERCENTAGE,
      },
      errorMessage: undefined,
    };
  }

  /**
   * Create a new meme token, initialize a DBC pool, AND execute initial buy - ALL IN ONE TRANSACTION.
   *
   * This is the "First-Swap-Min-Fee" feature that eliminates the snipe window for creators.
   * By bundling pool creation + first buy atomically:
   * - Creator gets tokens at minimum fee rate (first swap discount)
   * - No gap between pool creation and initial buy for snipers to exploit
   * - If buy fails, pool creation also fails (clean atomic failure)
   *
   * Uses SDK's createPoolWithFirstBuy() which returns a single combined Transaction
   * containing both pool creation and first buy instructions atomically.
   */
  async createMemeTokenAndPoolWithBuy(
    input: CreatePoolInput & { initialBuySol: number; slippageBps: number },
    sendTransaction: (transaction: Transaction, connection: Connection) => Promise<string>,
    onProgress?: (progress: TransactionProgress) => void
  ): Promise<CreatePoolResult & { initialBuy?: import('./types').InitialBuyResult }> {
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] ATOMIC POOL CREATION + FIRST BUY');
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Input:', {
      name: input.name,
      symbol: input.symbol,
      creator: input.creatorWallet.toBase58(),
      initialBuySol: input.initialBuySol,
      slippageBps: input.slippageBps,
    });

    const payer = input.creatorWallet;

    // Step 0: Validate config key on-chain
    DEBUG && console.log('[Meteora DBC] Step 0: Validating config key...');
    const configValidation = await this.validateConfigKey();

    if (configValidation.validationErrors.length > 0) {
      console.error('[Meteora DBC] Config validation errors:', configValidation.validationErrors);
    }

    // Determine the mint keypair - either vanity (pre-generated) or random
    const useVanity = !!input.vanityKeypair;
    let baseMintKeypair: Keypair | null = null;
    let baseMintPublicKey: PublicKey;

    if (useVanity) {
      baseMintPublicKey = new PublicKey(input.vanityKeypair!.publicKey);
      DEBUG && console.log('[Meteora DBC] Using vanity keypair:', baseMintPublicKey.toBase58());
    } else {
      console.warn('[Meteora DBC] ⚠️ USING RANDOM KEYPAIR - token will NOT have "dex" suffix!');
      baseMintKeypair = Keypair.generate();
      baseMintPublicKey = baseMintKeypair.publicKey;
    }

    const tokenUri = input.metadataUri || input.imageUrl || `https://api.dicebear.com/7.x/shapes/svg?seed=${input.symbol}`;

    const debugParams = {
      configKey: DEXTORO_DBC_CONFIG_KEY.toBase58(),
      baseMint: baseMintPublicKey.toBase58(),
      quoteMint: NATIVE_SOL_MINT.toBase58(),
      payer: payer.toBase58(),
      poolCreator: payer.toBase58(),
      name: input.name,
      symbol: input.symbol,
      uri: tokenUri,
      initialBuySol: input.initialBuySol,
      slippageBps: input.slippageBps,
      configDetails: configValidation,
      useVanity,
    };

    DEBUG && console.log('[Meteora DBC] Pool + buy parameters:', JSON.stringify(debugParams, null, 2));

    let poolAddress: string | null = null;
    let poolCreated = false;
    let txSignature: string = '';
    let errorMessage: string | undefined = undefined;
    let resendCount = 0;
    let tokensReceived: number | undefined = undefined;

    try {
      // Derive the expected pool address
      const derivedPoolAddress = deriveDbcPoolAddress(
        NATIVE_SOL_MINT,
        baseMintPublicKey,
        DEXTORO_DBC_CONFIG_KEY
      );
      DEBUG && console.log('[Meteora DBC] Expected pool address:', derivedPoolAddress.toBase58());

      // Calculate buy amounts
      const buyAmountLamports = new BN(Math.floor(input.initialBuySol * LAMPORTS_PER_SOL));

      // Get a quote to calculate minimum output with slippage
      // For initial buy, we estimate based on starting price (very low slippage expected)
      // Using 0 as minimum for the atomic transaction since we're first buyer
      const minimumAmountOut = new BN(0); // First buyer - accept any amount

      // Step 1: Create pool + buy transactions using SDK
      DEBUG && console.log('[Meteora DBC] Step 1: Creating atomic pool + buy transaction via SDK...');

      // SDK 1.5.6 returns a single combined Transaction with pool creation + first buy
      const atomicTx = await this.dbcClient.pool.createPoolWithFirstBuy({
        createPoolParam: {
          baseMint: baseMintPublicKey,
          config: DEXTORO_DBC_CONFIG_KEY,
          name: input.name,
          symbol: input.symbol,
          uri: tokenUri,
          payer: payer,
          poolCreator: payer,
        },
        firstBuyParam: {
          buyer: payer,
          buyAmount: buyAmountLamports,
          minimumAmountOut,
          referralTokenAccount: null,
        },
      });

      DEBUG && console.log('[Meteora DBC] Atomic tx instructions:', atomicTx.instructions.length);

      // Step 3: Send and confirm with retry logic
      DEBUG && console.log('[Meteora DBC] Step 2: Sending atomic transaction with HTTP confirmation...');

      const MAX_RETRIES = 3;
      const POLL_INTERVAL_MS = 1500;
      const ATTEMPT_TIMEOUT_MS = 60000;

      let confirmed = false;
      let lastError: string | null = null;

      while (!confirmed && resendCount <= MAX_RETRIES) {
        try {
          DEBUG && console.log(`[Meteora DBC] Attempt ${resendCount + 1}/${MAX_RETRIES + 1}: Fetching fresh blockhash...`);
          const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed');

          atomicTx.recentBlockhash = blockhash;
          atomicTx.feePayer = payer;
          atomicTx.signatures = [];

          // Sign with baseMint keypair
          if (useVanity && input.vanityKeypair) {
            DEBUG && console.log('[Meteora DBC] Getting vanity signature from server...');
            const txBase64 = Buffer.from(atomicTx.serializeMessage()).toString('base64');
            const vanitySignature = await input.vanityKeypair.signTransaction(txBase64);
            const signatureBytes = decodeBase58(vanitySignature);
            atomicTx.addSignature(baseMintPublicKey, Buffer.from(signatureBytes));
            DEBUG && console.log('[Meteora DBC] Vanity signature applied');
          } else if (baseMintKeypair) {
            atomicTx.partialSign(baseMintKeypair);
          }

          onProgress?.({
            stage: 'sending',
            signature: null,
            blockhash,
            lastValidBlockHeight,
            currentBlockHeight: null,
            resendCount,
            lastError,
            startTime: Date.now(),
            confirmations: null,
          });

          DEBUG && console.log('[Meteora DBC] Sending atomic transaction...');
          txSignature = await sendTransaction(atomicTx, this.connection);
          DEBUG && console.log('[Meteora DBC] Atomic tx sent:', txSignature);

          onProgress?.({
            stage: 'confirming',
            signature: txSignature,
            blockhash,
            lastValidBlockHeight,
            currentBlockHeight: null,
            resendCount,
            lastError: null,
            startTime: Date.now(),
            confirmations: null,
          });

          // HTTP-based polling for confirmation
          const confirmResult = await this.pollForConfirmationHTTP(
            txSignature,
            lastValidBlockHeight,
            POLL_INTERVAL_MS,
            ATTEMPT_TIMEOUT_MS,
            (currentBlockHeight, confirmations) => {
              onProgress?.({
                stage: 'confirming',
                signature: txSignature,
                blockhash,
                lastValidBlockHeight,
                currentBlockHeight,
                resendCount,
                lastError: null,
                startTime: Date.now(),
                confirmations,
              });
            }
          );

          if (confirmResult.confirmed) {
            confirmed = true;
            DEBUG && console.log('[Meteora DBC] Atomic transaction confirmed!');
            onProgress?.({
              stage: 'confirmed',
              signature: txSignature,
              blockhash,
              lastValidBlockHeight,
              currentBlockHeight: null,
              resendCount,
              lastError: null,
              startTime: Date.now(),
              confirmations: confirmResult.confirmations,
            });
          } else if (confirmResult.error) {
            throw new Error(confirmResult.error);
          } else if (confirmResult.blockHeightExceeded) {
            lastError = 'Block height exceeded';
            DEBUG && console.log('[Meteora DBC] Block height exceeded, retrying...');
            resendCount++;
          }
        } catch (attemptError) {
          const errorMsg = attemptError instanceof Error ? attemptError.message : 'Unknown error';
          const errorLogs = (attemptError as { logs?: string[] })?.logs;
          const allErrorText = [errorMsg, ...(errorLogs || [])].join(' ');
          lastError = errorMsg;

          if (allErrorText.includes('already in use')) {
            console.error('[Meteora DBC] Mint account already exists on-chain');
            if (useVanity && input.vanityKeypair) {
              try {
                await input.vanityKeypair.markAsUsed(baseMintPublicKey.toBase58(), 'already-in-use-on-chain');
              } catch (markErr) {
                console.warn('[Meteora DBC] Failed to mark vanity keypair as used:', markErr);
              }
            }
            throw new Error(
              'This mint address was already used on-chain. Please try again.'
            );
          }

          const isRecoverable = this.isRecoverableError(errorMsg);
          if (!isRecoverable) {
            throw attemptError;
          }

          console.warn(`[Meteora DBC] Recoverable error on attempt ${resendCount + 1}:`, errorMsg);
          resendCount++;
        }
      }

      if (!confirmed) {
        throw new Error(`Atomic transaction failed after ${resendCount} retries: ${lastError || 'Max retries exceeded'}`);
      }

      // Step 4: Verify pool creation and calculate tokens received
      DEBUG && console.log('[Meteora DBC] Step 3: Verifying pool creation...');

      await new Promise(resolve => setTimeout(resolve, 2000));

      const verifyPool = await this.dbcClient.state.getPoolByBaseMint(baseMintPublicKey);

      if (!verifyPool) {
        console.warn('[Meteora DBC] Pool not found immediately after creation - may still be indexing');
        poolAddress = derivedPoolAddress.toBase58();
        poolCreated = true;
      } else {
        poolAddress = verifyPool.publicKey.toBase58();
        poolCreated = true;
        DEBUG && console.log('[Meteora DBC] Pool verified on-chain:', poolAddress);
      }

      // Fetch actual tokens received from confirmed transaction
      // Parse post-token-balances to get exact amount the buyer received
      try {
        const parsedTx = await this.connection.getParsedTransaction(txSignature, {
          maxSupportedTransactionVersion: 0,
          commitment: 'confirmed',
        });

        if (parsedTx?.meta?.postTokenBalances) {
          const mintStr = baseMintPublicKey.toBase58();
          const payerStr = payer.toBase58();
          const tokenBalance = parsedTx.meta.postTokenBalances.find(
            (b) => b.mint === mintStr && b.owner === payerStr
          );
          if (tokenBalance?.uiTokenAmount?.uiAmount) {
            tokensReceived = tokenBalance.uiTokenAmount.uiAmount;
            DEBUG && console.log('[Meteora DBC] Actual tokens from tx:', tokensReceived);
          }
        }
      } catch (parseErr) {
        console.warn('[Meteora DBC] Could not parse tx for token amount:', parseErr);
      }

      // Fallback to bonding curve estimate if parsing failed
      if (tokensReceived <= 0) {
        const estimate = await this.estimateInitialBuyTokens(input.initialBuySol);
        tokensReceived = estimate?.tokens ?? 0;
        console.warn('[Meteora DBC] Using estimated token amount (fallback):', tokensReceived);
      }

      DEBUG && console.log('[Meteora DBC] ========================================');
      DEBUG && console.log('[Meteora DBC] ATOMIC SUCCESS!');
      DEBUG && console.log('[Meteora DBC] ========================================');
      DEBUG && console.log('[Meteora DBC] Mint:', baseMintPublicKey.toBase58());
      DEBUG && console.log('[Meteora DBC] Pool:', poolAddress);
      DEBUG && console.log('[Meteora DBC] Tx:', txSignature);
      DEBUG && console.log('[Meteora DBC] Initial Buy SOL:', input.initialBuySol);
      DEBUG && console.log('[Meteora DBC] Estimated tokens:', tokensReceived);

      // Mark vanity keypair as used
      if (useVanity && input.vanityKeypair) {
        try {
          await input.vanityKeypair.markAsUsed(baseMintPublicKey.toBase58(), txSignature);
          DEBUG && console.log('[Meteora DBC] Vanity keypair marked as used');
        } catch (markError) {
          console.warn('[Meteora DBC] Failed to mark vanity keypair as used:', markError);
        }
      }

    } catch (error: unknown) {
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] ATOMIC POOL + BUY FAILED');
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] Full error:', error);

      let extractedMessage = 'Unknown error';
      if (error instanceof Error) {
        extractedMessage = error.message;
      }

      let programLogs: string[] = [];
      const errorWithLogs = error as { logs?: string[] };
      if (errorWithLogs.logs && Array.isArray(errorWithLogs.logs)) {
        programLogs = errorWithLogs.logs;
        const errorLog = errorWithLogs.logs.find(
          (log) => log.includes('Error') || log.includes('failed') || log.includes('insufficient')
        );
        if (errorLog) {
          extractedMessage = errorLog;
        }
      }

      errorMessage = [
        'Atomic pool + buy creation failed.',
        '',
        `Reason: ${extractedMessage}`,
        '',
        programLogs.length > 0 ? `Program logs (last 10):\n${programLogs.slice(-10).join('\n')}` : '',
      ].join('\n');

      // Handle vanity keypair cleanup
      const allLogText = [...programLogs, extractedMessage].join(' ');
      const isAlreadyInUse = allLogText.includes('already in use');

      if (useVanity && input.vanityKeypair && !txSignature && !isAlreadyInUse) {
        try {
          await input.vanityKeypair.releaseOnFailure();
          DEBUG && console.log('[Meteora DBC] Vanity keypair released back to pool');
        } catch (releaseError) {
          console.warn('[Meteora DBC] Failed to release vanity keypair:', releaseError);
        }
      }

      return {
        mintAddress: '',
        poolAddress: null,
        poolCreated: false,
        metadataCreated: false,
        txSignature: txSignature || 'No transaction sent',
        poolConfig: {
          curveType: 'linear',
          protocolFeePercent: 1,
          creatorFeePercent: CREATOR_TRADING_FEE_PERCENTAGE,
          partnerFeePercent: PARTNER_TRADING_FEE_PERCENTAGE,
          migrationFeePercent: 10,
          creatorMigrationFeePercent: CREATOR_MIGRATION_FEE_PERCENTAGE,
          partnerMigrationFeePercent: PARTNER_MIGRATION_FEE_PERCENTAGE,
        },
        errorMessage,
        initialBuy: {
          executed: true,
          success: false,
          error: extractedMessage,
        },
      };
    }

    return {
      mintAddress: baseMintPublicKey.toBase58(),
      poolAddress: poolAddress,
      poolCreated: poolCreated,
      metadataCreated: poolCreated,
      txSignature: txSignature,
      poolConfig: {
        curveType: 'linear',
        protocolFeePercent: 1,
        creatorFeePercent: CREATOR_TRADING_FEE_PERCENTAGE,
        partnerFeePercent: PARTNER_TRADING_FEE_PERCENTAGE,
        migrationFeePercent: 10,
        creatorMigrationFeePercent: CREATOR_MIGRATION_FEE_PERCENTAGE,
        partnerMigrationFeePercent: PARTNER_MIGRATION_FEE_PERCENTAGE,
      },
      errorMessage: undefined,
      initialBuy: {
        executed: true,
        success: true,
        txSignature: txSignature,
        tokensReceived: tokensReceived,
        solSpent: input.initialBuySol,
      },
    };
  }

  /**
   * Poll for transaction confirmation using HTTP (getSignatureStatuses)
   * Does not rely on WebSocket subscriptions
   */
  private async pollForConfirmationHTTP(
    signature: string,
    lastValidBlockHeight: number,
    pollIntervalMs: number,
    timeoutMs: number,
    onProgress: (currentBlockHeight: number, confirmations: number | null) => void
  ): Promise<{ confirmed: boolean; blockHeightExceeded: boolean; error: string | null; confirmations: number | null }> {
    const startTime = Date.now();
    let pollCount = 0;

    while (Date.now() - startTime < timeoutMs) {
      pollCount++;
      
      try {
        // Check signature status via HTTP
        const statusResponse = await this.connection.getSignatureStatuses([signature]);
        const status = statusResponse?.value?.[0] ?? null;

        // Get current block height
        const currentBlockHeight = await this.connection.getBlockHeight('confirmed');
        onProgress(currentBlockHeight, status?.confirmations ?? null);

        // Check if we've exceeded block height
        if (currentBlockHeight > lastValidBlockHeight) {
          DEBUG && console.log(`[Meteora DBC] Block height exceeded: ${currentBlockHeight} > ${lastValidBlockHeight}`);
          return { 
            confirmed: false, 
            blockHeightExceeded: true, 
            error: null,
            confirmations: null,
          };
        }

        if (status) {
          // Check for error
          if (status.err) {
            const errorStr = JSON.stringify(status.err);
            console.error('[Meteora DBC] Transaction error:', errorStr);
            return { 
              confirmed: false, 
              blockHeightExceeded: false, 
              error: `Transaction failed on-chain: ${errorStr}`,
              confirmations: null,
            };
          }

          // Check confirmation status
          const confirmedStatus = status.confirmationStatus;
          if (
            confirmedStatus === 'confirmed' || 
            confirmedStatus === 'finalized' ||
            (status.confirmations ?? 0) > 0
          ) {
            return { 
              confirmed: true, 
              blockHeightExceeded: false, 
              error: null,
              confirmations: status.confirmations ?? null,
            };
          }
        }

        // Log progress periodically
        if (pollCount % 5 === 0) {
          DEBUG && console.log(`[Meteora DBC] Polling... attempt ${pollCount}, block ${currentBlockHeight}/${lastValidBlockHeight}`);
        }

      } catch (error) {
        // Network error during polling - continue trying
        console.warn('[Meteora DBC] Poll error:', error);
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    // Timeout reached
    console.warn('[Meteora DBC] Polling timeout reached');
    return { 
      confirmed: false, 
      blockHeightExceeded: true, // Treat timeout as block height issue
      error: null,
      confirmations: null,
    };
  }

  /**
   * Check if an error is recoverable (worth retrying)
   */
  private isRecoverableError(errorMessage: string): boolean {
    const lowerMessage = errorMessage.toLowerCase();

    // Non-recoverable errors - fail immediately without retry
    // These are validation/auth errors that won't succeed on retry
    const nonRecoverablePatterns = [
      'invalid transaction',     // Validation failures (e.g., wrong program ID)
      'invalid signature',       // Auth failures
      'keypair not found',       // Vanity keypair issues
      'not reserved',            // Auth failures
      'already been used',       // Keypair reuse
      'unauthorized',            // Auth failures
      '401',                     // HTTP Unauthorized
      '403',                     // HTTP Forbidden
      '404',                     // Not found
      '409',                     // Conflict (already used)
    ];

    if (nonRecoverablePatterns.some(pattern => lowerMessage.includes(pattern.toLowerCase()))) {
      return false;
    }

    // Recoverable errors - worth retrying
    const recoverablePatterns = [
      'block height exceeded',
      'blockhash not found',
      'transaction expired',
      'network error',
      'fetch failed',
      'timeout',
      'connection refused',
      'ECONNRESET',
      'ETIMEDOUT',
    ];

    return recoverablePatterns.some(pattern => lowerMessage.includes(pattern.toLowerCase()));
  }

  /**
   * Find pool for a token mint by iterating historical DBC config keys.
   * Each coin's pool PDA is derived from [WSOL, baseMint, configKey] — when
   * configs were retired, older coins remain pinned to the config they were
   * created against. We try each historical config in order and return the
   * first PDA whose account exists. Once resolved, the config is cached per
   * mint so subsequent lookups skip the iteration.
   *
   * PDA seeds: [quoteMint (WSOL), baseMint (token), configKey]
   */
  async getPoolForMint(baseMint: PublicKey): Promise<{ exists: boolean; poolAddress?: PublicKey; poolState?: any; config?: PublicKey }> {
    const baseMintStr = baseMint.toBase58();
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Looking up pool for mint:', baseMintStr);
    DEBUG && console.log('[Meteora DBC] Quote mint (WSOL):', NATIVE_SOL_MINT.toBase58());

    // If we've already resolved which config this mint uses, try it directly.
    const cachedConfig = MeteoraDBCClient.mintToConfigCache.get(baseMintStr);
    const configsToTry = cachedConfig
      ? [cachedConfig]
      : DEXTORO_DBC_CONFIG_KEYS_HISTORICAL;

    try {
      for (const configKey of configsToTry) {
        const derivedPoolAddress = deriveDbcPoolAddress(
          NATIVE_SOL_MINT, // quote mint (WSOL)
          baseMint,        // base mint (token)
          configKey,
        );

        DEBUG && console.log(
          `[Meteora DBC] Trying config ${configKey.toBase58()} -> PDA ${derivedPoolAddress.toBase58()}`,
        );

        const accountInfo = await this.connection.getAccountInfo(derivedPoolAddress);
        if (!accountInfo) continue;

        DEBUG && console.log('[Meteora DBC] Pool account found! Size:', accountInfo.data.length, 'bytes');
        console.log(
          `[Meteora DBC] Resolved mint ${baseMintStr} to config ${configKey.toBase58()}`,
        );
        MeteoraDBCClient.mintToConfigCache.set(baseMintStr, configKey);

        // Try to fetch pool state via SDK for proper deserialization
        try {
          const poolAccount = await this.dbcClient.state.getPool(derivedPoolAddress);
          DEBUG && console.log('[Meteora DBC] Pool state fetched successfully');
          DEBUG && console.log('[Meteora DBC] ========================================');
          return {
            exists: true,
            poolAddress: derivedPoolAddress,
            poolState: poolAccount,
            config: configKey,
          };
        } catch (parseError) {
          // Account exists but couldn't parse — still counts as found
          console.warn('[Meteora DBC] Pool account exists but could not parse state:', parseError);
          return {
            exists: true,
            poolAddress: derivedPoolAddress,
            poolState: null,
            config: configKey,
          };
        }
      }

      DEBUG && console.log('[Meteora DBC] No pool account found across', configsToTry.length, 'config(s)');
      DEBUG && console.log('[Meteora DBC] ========================================');
      return { exists: false };
    } catch (error) {
      console.error('[Meteora DBC] Error looking up pool:', error);
      DEBUG && console.log('[Meteora DBC] ========================================');
      return { exists: false };
    }
  }

  /**
   * Fetch pool information from chain
   * Uses direct PDA derivation with our config key (not generic getPoolByBaseMint)
   * Returns null only if pool does not exist. Returns partial data with hasSafeStats=false
   * if stats cannot be safely converted.
   */
  async getPoolInfo(poolAddressOrMint: string): Promise<PoolInfo | null> {
    DEBUG && console.log('[Meteora DBC] Fetching pool info for:', poolAddressOrMint);

    try {
      const mintPubkey = new PublicKey(poolAddressOrMint);
      
      // Use our new direct PDA lookup method
      const poolLookup = await this.getPoolForMint(mintPubkey);
      
      if (!poolLookup.exists || !poolLookup.poolAddress) {
        DEBUG && console.log('[Meteora DBC] No pool found for mint:', poolAddressOrMint);
        return null;
      }

      const poolAddress = poolLookup.poolAddress.toBase58();
      const resolvedConfig = poolLookup.config?.toBase58();

      // If we couldn't parse the state, return minimal info with hasSafeStats=false
      if (!poolLookup.poolState) {
        DEBUG && console.log('[Meteora DBC] Pool exists but state not parsed, returning minimal info');
        return {
          poolAddress,
          mintAddress: poolAddressOrMint,
          name: 'Coin',
          symbol: 'TKN',
          imageUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=token',
          description: '',
          currentPrice: null,
          virtualFDV: null,
          reserveBase: null,
          reserveQuote: null,
          totalSupply: 1_000_000_000,
          circulatingSupply: null,
          holders: null,
          volume24h: null,
          priceChange24h: null,
          createdAt: new Date(),
          creator: '',
          isMigrated: false,
          migrationProgress: null,
          hasSafeStats: false,
          config: resolvedConfig,
        };
      }

      const poolState = poolLookup.poolState;

      // Safely calculate stats
      let currentPrice: number | null = null;
      let baseReserve: number | null = null;
      let quoteReserve: number | null = null;
      let virtualFDV: number | null = null;
      let hasSafeStats = true;

      try {
        // Calculate price from sqrt price
        DEBUG && console.log('[Meteora DBC] sqrtPrice raw value:', poolState.sqrtPrice?.toString?.() || poolState.sqrtPrice);
        DEBUG && console.log('[Meteora DBC] sqrtPrice type:', typeof poolState.sqrtPrice);

        // Per-pool tokenDecimal: different DBC configs use different decimals
        // (v5=6, v7-stealth=9). Substituting the active config's decimal here
        // produces silent 10^(active - actual) errors in price + reserveBase.
        // Quote token (SOL) is always 9 decimals.
        let baseDecimal: number;
        if (resolvedConfig) {
          const perConfig = await this.getTokenDecimalForConfig(resolvedConfig);
          baseDecimal = perConfig !== null ? perConfig : await this.ensureConfigTokenDecimal();
        } else {
          baseDecimal = await this.ensureConfigTokenDecimal();
        }
        const baseTokenDecimal = baseDecimal === 6 ? TokenDecimal.SIX :
                                 baseDecimal === 7 ? TokenDecimal.SEVEN :
                                 baseDecimal === 8 ? TokenDecimal.EIGHT : TokenDecimal.NINE;
        DEBUG && console.log('[Meteora DBC] Using baseDecimal:', baseDecimal, 'quoteDecimal: 9', 'for config:', resolvedConfig ?? '(active)');

        const price = getPriceFromSqrtPrice(
          poolState.sqrtPrice,
          baseTokenDecimal,
          TokenDecimal.NINE  // SOL is always 9 decimals
        );
        DEBUG && console.log('[Meteora DBC] getPriceFromSqrtPrice returned:', price?.toString?.() || price);

        currentPrice = safeNumber(price);
        DEBUG && console.log('[Meteora DBC] safeNumber(price) returned:', currentPrice);

        // Scale raw reserves to floats in string domain — handles v7-stealth
        // virtual reserves that exceed 2^53 (where safeNumber+division fails).
        baseReserve = rawAmountToScaled(poolState.baseReserve?.toString?.() ?? '0', baseDecimal);
        quoteReserve = rawAmountToScaled(poolState.quoteReserve?.toString?.() ?? '0', 9);

        DEBUG && console.log('[Meteora DBC] Reserve raw values:', {
          baseReserveType: typeof poolState.baseReserve,
          baseReserveStr: poolState.baseReserve?.toString?.(),
          quoteReserveType: typeof poolState.quoteReserve,
          quoteReserveStr: poolState.quoteReserve?.toString?.(),
          baseReserveScaled: baseReserve,
          quoteReserveScaled: quoteReserve,
        });

        // FDV = price * total supply (1B tokens)
        const totalSupply = 1_000_000_000;
        virtualFDV = currentPrice !== null ? currentPrice * totalSupply : null;

        DEBUG && console.log('[Meteora DBC] Calculated values:', {
          currentPrice,
          virtualFDV,
          baseReserve,
          quoteReserve,
        });

        // Check if any critical values are null
        if (currentPrice === null || baseReserve === null || quoteReserve === null) {
          hasSafeStats = false;
          console.warn('[Meteora DBC] Some pool stats could not be safely converted');
        }
      } catch (calcError) {
        console.error('[Meteora DBC] Error calculating pool stats:', calcError);
        hasSafeStats = false;
      }

      const migrationProgress = safeNumber(poolState.migrationProgress);

      DEBUG && console.log('[Meteora DBC] Pool info:', {
        poolAddress,
        price: currentPrice,
        baseReserve,
        quoteReserve,
        isMigrated: poolState.isMigrated,
        hasSafeStats,
      });

      return {
        poolAddress,
        mintAddress: poolState.baseMint.toBase58(),
        name: 'Coin',
        symbol: 'TKN',
        imageUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=token',
        description: '',
        currentPrice,
        virtualFDV,
        reserveBase: baseReserve,
        reserveQuote: quoteReserve,
        totalSupply: 1_000_000_000,
        circulatingSupply: baseReserve,
        holders: null, // Not available from pool state
        volume24h: null, // Not available from pool state
        priceChange24h: null, // Not available from pool state
        createdAt: new Date(),
        creator: poolState.creator.toBase58(),
        isMigrated: poolState.isMigrated === 1 || poolState.isMigrated === true,
        migrationProgress,
        hasSafeStats,
        config: resolvedConfig,
      };
    } catch (error) {
      console.error('[Meteora DBC] Error fetching pool info:', error);
      return null;
    }
  }

  /**
   * Batch fetch pool info for multiple mints in a single RPC call.
   * Derives all pool PDAs, fetches accounts via getMultipleAccountsInfo,
   * then decodes each using the Anchor program coder.
   */
  async getPoolInfoBatch(mints: string[]): Promise<Map<string, PoolInfo>> {
    const result = new Map<string, PoolInfo>();
    if (mints.length === 0) return result;

    try {
      // Derive a pool PDA per (mint, historical config). For each mint we keep
      // a list of (config, PDA) candidates and pick the first whose account
      // exists. Most mints resolve on the first config (v3); legacy mints
      // (v2 etc.) hit the next slot.
      const mintPubkeys = mints.map((m) => new PublicKey(m));
      const candidatesPerMint: { configKey: PublicKey; pda: PublicKey }[][] =
        mintPubkeys.map((mint) => {
          const cached = MeteoraDBCClient.mintToConfigCache.get(mint.toBase58());
          const configs = cached
            ? [cached]
            : DEXTORO_DBC_CONFIG_KEYS_HISTORICAL;
          return configs.map((configKey) => ({
            configKey,
            pda: deriveDbcPoolAddress(NATIVE_SOL_MINT, mint, configKey),
          }));
        });

      // Flatten for a single multi-fetch and remember which mint owns each slot.
      const flatPDAs: PublicKey[] = [];
      const flatOwners: { mintIndex: number; configKey: PublicKey }[] = [];
      for (let i = 0; i < candidatesPerMint.length; i++) {
        for (const c of candidatesPerMint[i]) {
          flatPDAs.push(c.pda);
          flatOwners.push({ mintIndex: i, configKey: c.configKey });
        }
      }

      // Fetch all accounts in chunks of 100
      const MAX_ACCOUNTS_PER_CALL = 100;
      const flatInfos: (import('@solana/web3.js').AccountInfo<Buffer> | null)[] = [];
      for (let i = 0; i < flatPDAs.length; i += MAX_ACCOUNTS_PER_CALL) {
        const batch = flatPDAs.slice(i, i + MAX_ACCOUNTS_PER_CALL);
        const infos = await this.connection.getMultipleAccountsInfo(batch);
        flatInfos.push(...infos);
      }

      // Reduce: for each mint, take the first matching candidate.
      const allAccountInfos: (import('@solana/web3.js').AccountInfo<Buffer> | null)[] =
        new Array(mints.length).fill(null);
      const poolPDAs: PublicKey[] = new Array(mints.length).fill(null) as unknown as PublicKey[];
      const resolvedConfigByIndex: (PublicKey | null)[] = new Array(mints.length).fill(null);
      for (let i = 0; i < flatOwners.length; i++) {
        const info = flatInfos[i];
        if (!info) continue;
        const { mintIndex, configKey } = flatOwners[i];
        if (allAccountInfos[mintIndex]) continue; // already resolved
        allAccountInfos[mintIndex] = info;
        poolPDAs[mintIndex] = flatPDAs[i];
        resolvedConfigByIndex[mintIndex] = configKey;
        MeteoraDBCClient.mintToConfigCache.set(mints[mintIndex], configKey);
      }
      // Fill PDA slots that didn't resolve, so the loop below can still log
      // a stable address.
      for (let i = 0; i < poolPDAs.length; i++) {
        if (!poolPDAs[i]) {
          poolPDAs[i] = candidatesPerMint[i][0]?.pda;
        }
      }

      // Active-config baseDecimal — used as a fallback only. Per-pool decimal
      // is resolved inside the loop from each pool's actual config.
      const activeBaseDecimal = await this.ensureConfigTokenDecimal();

      // Get the Anchor program to decode accounts
      const program = this.dbcClient.state.getProgram();

      for (let i = 0; i < mints.length; i++) {
        const accountInfo = allAccountInfos[i];
        const mintAddress = mints[i];
        const poolAddress = poolPDAs[i].toBase58();
        const resolvedConfig = resolvedConfigByIndex[i]?.toBase58();

        if (!accountInfo || !accountInfo.data) {
          // Pool does not exist for this mint
          continue;
        }

        try {
          // Decode pool state from raw account data using Anchor coder
          const poolState = program.coder.accounts.decode('virtualPool', accountInfo.data);

          // Per-pool tokenDecimal: v5 pools use 6, v7-stealth uses 9. Cache
          // populates after first fetch per unique config, so amortized cost
          // is one extra RPC per batch.
          let baseDecimal: number = activeBaseDecimal;
          if (resolvedConfig) {
            const perConfig = await this.getTokenDecimalForConfig(resolvedConfig);
            if (perConfig !== null) baseDecimal = perConfig;
          }
          const baseTokenDecimal = baseDecimal === 6 ? TokenDecimal.SIX :
                                   baseDecimal === 7 ? TokenDecimal.SEVEN :
                                   baseDecimal === 8 ? TokenDecimal.EIGHT : TokenDecimal.NINE;

          // Calculate price from sqrtPrice
          let currentPrice: number | null = null;
          let baseReserve: number | null = null;
          let quoteReserve: number | null = null;
          let virtualFDV: number | null = null;
          let hasSafeStats = true;

          try {
            const price = getPriceFromSqrtPrice(
              poolState.sqrtPrice,
              baseTokenDecimal,
              TokenDecimal.NINE
            );
            currentPrice = safeNumber(price);

            baseReserve = rawAmountToScaled(poolState.baseReserve?.toString?.() ?? '0', baseDecimal);
            quoteReserve = rawAmountToScaled(poolState.quoteReserve?.toString?.() ?? '0', 9);

            const totalSupply = 1_000_000_000;
            virtualFDV = currentPrice !== null ? currentPrice * totalSupply : null;

            if (currentPrice === null || baseReserve === null || quoteReserve === null) {
              hasSafeStats = false;
            }
          } catch {
            hasSafeStats = false;
          }

          const migrationProgress = safeNumber(poolState.migrationProgress);

          result.set(mintAddress, {
            poolAddress,
            mintAddress: poolState.baseMint?.toBase58?.() ?? mintAddress,
            name: 'Coin',
            symbol: 'TKN',
            imageUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=token',
            description: '',
            currentPrice,
            virtualFDV,
            reserveBase: baseReserve,
            reserveQuote: quoteReserve,
            totalSupply: 1_000_000_000,
            circulatingSupply: baseReserve,
            holders: null,
            volume24h: null,
            priceChange24h: null,
            createdAt: new Date(),
            creator: poolState.creator?.toBase58?.() ?? '',
            isMigrated: poolState.isMigrated === 1 || poolState.isMigrated === true,
            migrationProgress,
            hasSafeStats,
            config: resolvedConfig,
          });
        } catch (err) {
          DEBUG && console.warn(`[Meteora DBC] Failed to decode pool for ${mintAddress}:`, err);
        }
      }

      DEBUG && console.log(`[Meteora DBC] Batch fetched ${result.size}/${mints.length} pools`);
      return result;
    } catch (error) {
      console.error('[Meteora DBC] Error in getPoolInfoBatch:', error);
      return result;
    }
  }

  /**
   * Get quote for buying tokens from the curve
   */
  async getBuyQuote(poolAddressOrMint: string, amountInSol: number, slippageBps: number): Promise<TradeQuote> {
    try {
      // Use cached pool state when available (0 RPC calls on cache hit)
      const cached = await this.getCachedPoolState(poolAddressOrMint);

      if (!cached) {
        return {
          amountOut: 0,
          priceImpact: 0,
          fee: 0,
          minAmountOut: 0,
          error: 'POOL_NOT_FOUND',
          errorMessage: 'Pool not found',
        };
      }

      const { poolState, config } = cached;

      // Check pool liquidity before calculating quote
      const quoteReserveSol = rawAmountToScaled(poolState.quoteReserve?.toString?.() ?? '0', 9) ?? 0;

      const amountInLamports = new BN(Math.floor(amountInSol * LAMPORTS_PER_SOL));

      // Use the SDK's swap quote function
      // Safely convert activationPoint - it may be a BN/BigInt that exceeds safe range
      let currentPointBN = new BN(0);
      try {
        const pointStr = poolState.activationPoint?.toString?.() ?? '0';
        currentPointBN = new BN(pointStr);
      } catch {
        console.warn('[Meteora DBC] Failed to convert activationPoint, using 0');
      }

      const quote = this.dbcClient.pool.swapQuote({
        virtualPool: poolState,
        config,
        swapBaseForQuote: false, // Buying base (token) with quote (SOL)
        amountIn: amountInLamports,
        slippageBps,
        hasReferral: false,
        currentPoint: currentPointBN,
      });

      // Buy output is in base token smallest units (6 decimals).
      // Trading fee for buy is in SOL lamports (input token).
      const outputAmount = rawAmountToScaled(quote.outputAmount.toString(), BASE_TOKEN_DECIMALS) ?? 0;
      const tradingFee = rawAmountToScaled(quote.tradingFee.toString(), 9) ?? 0;
      const minOutput = rawAmountToScaled(quote.minimumAmountOut.toString(), BASE_TOKEN_DECIMALS) ?? 0;

      // Post-quote liquidity check: if SDK returns zero output, pool truly has no liquidity
      if (outputAmount === 0) {
        return {
          amountOut: 0,
          priceImpact: 0,
          fee: 0,
          minAmountOut: 0,
          error: 'INSUFFICIENT_LIQUIDITY',
          errorMessage: 'Insufficient liquidity in pool',
        };
      }

      // Calculate price impact based on actual pool reserves
      // price_impact = (input / (pool_reserve + input)) * 100
      const priceImpact = quoteReserveSol > 0
        ? Math.min(100, (amountInSol / (quoteReserveSol + amountInSol)) * 100)
        : 100;

      return {
        amountOut: outputAmount,
        priceImpact: Math.min(100, Math.abs(priceImpact)), // Cap at 100%
        fee: tradingFee,
        minAmountOut: minOutput,
      };
    } catch (error) {
      console.error('[Meteora DBC] Error getting buy quote:', error);
      return {
        amountOut: 0,
        priceImpact: 0,
        fee: 0,
        minAmountOut: 0,
        error: 'POOL_NOT_FOUND',
        errorMessage: 'Failed to get quote',
      };
    }
  }

  /**
   * Get quote for selling tokens to the curve
   */
  async getSellQuote(poolAddressOrMint: string, amountInTokens: number, slippageBps: number): Promise<TradeQuote> {
    try {
      // Use cached pool state when available (0 RPC calls on cache hit)
      const cached = await this.getCachedPoolState(poolAddressOrMint);

      if (!cached) {
        return {
          amountOut: 0,
          priceImpact: 0,
          fee: 0,
          minAmountOut: 0,
          error: 'POOL_NOT_FOUND',
          errorMessage: 'Pool not found',
        };
      }

      const { poolState, config } = cached;

      // Check pool liquidity before calculating quote
      const quoteReserveSol = rawAmountToScaled(poolState.quoteReserve?.toString?.() ?? '0', 9) ?? 0;

      const amountInSmallest = new BN(Math.floor(amountInTokens * BASE_TOKEN_MULTIPLIER));

      // Safely convert activationPoint - it may be a BN/BigInt that exceeds safe range
      let currentPointBN = new BN(0);
      try {
        const pointStr = poolState.activationPoint?.toString?.() ?? '0';
        currentPointBN = new BN(pointStr);
      } catch {
        console.warn('[Meteora DBC] Failed to convert activationPoint, using 0');
      }

      const quote = this.dbcClient.pool.swapQuote({
        virtualPool: poolState,
        config,
        swapBaseForQuote: true, // Selling base (token) for quote (SOL)
        amountIn: amountInSmallest,
        slippageBps,
        hasReferral: false,
        currentPoint: currentPointBN,
      });

      // Sell output, trading fee, and min output are all in SOL lamports.
      const outputAmount = rawAmountToScaled(quote.outputAmount.toString(), 9) ?? 0;
      const tradingFee = rawAmountToScaled(quote.tradingFee.toString(), 9) ?? 0;
      const minOutput = rawAmountToScaled(quote.minimumAmountOut.toString(), 9) ?? 0;

      // Post-quote liquidity check: if SDK returns zero output, pool truly has no liquidity
      if (outputAmount === 0) {
        return {
          amountOut: 0,
          priceImpact: 0,
          fee: 0,
          minAmountOut: 0,
          error: 'INSUFFICIENT_LIQUIDITY',
          errorMessage: 'Insufficient liquidity in pool',
        };
      }

      // Calculate price impact based on actual pool reserves
      // For sells: impact = (output / (pool_reserve)) * 100
      const priceImpact = quoteReserveSol > 0
        ? Math.min(100, (outputAmount / quoteReserveSol) * 100)
        : 100;

      return {
        amountOut: outputAmount,
        priceImpact: Math.min(100, Math.abs(priceImpact)), // Cap at 100%
        fee: tradingFee,
        minAmountOut: minOutput,
      };
    } catch (error) {
      console.error('[Meteora DBC] Error getting sell quote:', error);
      return {
        amountOut: 0,
        priceImpact: 0,
        fee: 0,
        minAmountOut: 0,
        error: 'POOL_NOT_FOUND',
        errorMessage: 'Failed to get quote',
      };
    }
  }

  /**
   * Buy tokens from the DBC pool
   * 
   * FEES:
   * - Protocol fees: Handled by the on-chain config (goes to protocol fee owner)
   * - Referral fees: Routed to treasury WSOL ATA via referralTokenAccount
   */
  async buyFromCurve(
    input: BuyFromCurveInput,
    sendTransaction: (transaction: Transaction, connection: Connection) => Promise<string>
  ): Promise<TradeResult> {
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] BUY FROM CURVE');
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Input mint address:', input.poolAddress);
    DEBUG && console.log('[Meteora DBC] Amount in SOL:', input.amountInSol);
    DEBUG && console.log('[Meteora DBC] Amount in lamports:', Math.floor(input.amountInSol * LAMPORTS_PER_SOL));
    DEBUG && console.log('[Meteora DBC] Slippage BPS:', input.slippageBps);
    DEBUG && console.log('[Meteora DBC] User wallet:', input.userWallet.toBase58());
    DEBUG && console.log('[Meteora DBC] Quote mint (WSOL):', NATIVE_SOL_MINT.toBase58());
    DEBUG && console.log('[Meteora DBC] Treasury wallet:', DEXTORO_TREASURY_WALLET.toBase58());

    try {
      // Use cached pool state (0 RPC calls on cache hit)
      const cached = await this.getCachedPoolState(input.poolAddress);

      if (!cached) {
        console.error('[Meteora DBC] POOL NOT FOUND - cannot proceed with buy');
        throw new Error('Pool not found for this coin. Trading is not yet available.');
      }

      const poolAddress = cached.poolAddress;
      const amountInLamports = new BN(Math.floor(input.amountInSol * LAMPORTS_PER_SOL));

      DEBUG && console.log('[Meteora DBC] Pool found (cached):', poolAddress.toBase58());
      DEBUG && console.log('[Meteora DBC] Resolved config:', cached.poolState?.config?.toBase58?.() ?? 'unknown');

      // Use precomputed quote if available, otherwise fetch fresh
      const quote = input.precomputedQuote ?? await this.getBuyQuote(input.poolAddress, input.amountInSol, input.slippageBps);
      const minimumAmountOut = new BN(Math.floor(quote.minAmountOut * BASE_TOKEN_MULTIPLIER));

      DEBUG && console.log('[Meteora DBC] Quote:', {
        precomputed: !!input.precomputedQuote,
        amountOut: quote.amountOut,
        minAmountOut: quote.minAmountOut,
        priceImpact: quote.priceImpact,
        fee: quote.fee,
      });

      // Create the swap transaction
      const swapTx = await this.dbcClient.pool.swap({
        owner: input.userWallet,
        pool: poolAddress,
        amountIn: amountInLamports,
        minimumAmountOut,
        swapBaseForQuote: false, // false = buying tokens with SOL
        referralTokenAccount: null,
      });

      // Use cached blockhash when available
      const { blockhash, lastValidBlockHeight } = await this.getCachedBlockhash();
      swapTx.recentBlockhash = blockhash;
      swapTx.feePayer = input.userWallet;

      // Add compute budget + Jito tip for Helius Sender dual routing
      await prepareTransactionForSender(swapTx, input.userWallet, {
        priorityLevel: input.priorityLevel,
      });

      DEBUG && console.log('[Meteora DBC] Transaction prepared with Sender instructions');
      DEBUG && console.log('[Meteora DBC] Instructions count:', swapTx.instructions.length);
      DEBUG && console.log('[Meteora DBC] Sending buy transaction...');

      const signature = await sendTransaction(swapTx, this.connection);
      DEBUG && console.log('[Meteora DBC] Buy tx sent:', signature);
      DEBUG && console.log('[Meteora DBC] Solscan:', getSolscanUrl(signature, 'tx'));

      // Notify caller before waiting for confirmation (enables optimistic UI)
      input.onSent?.(signature, quote);

      // Use HTTP-based confirmation with aggressive polling for trades
      const confirmation = await confirmSignatureHttp(this.connection, signature, {
        commitment: 'confirmed',
        timeoutMs: 120000,
        label: 'buy-swap',
        pollIntervalFn: tradingPollInterval,
      });

      if (!confirmation.confirmed) {
        if (confirmation.pending) {
          // Transaction may still confirm - provide signature for user to check
          console.warn('[Meteora DBC] Buy transaction confirmation timed out but may still succeed');
          throw new Error(`Transaction sent but confirmation timed out. Check signature: ${signature}`);
        }
        console.error('[Meteora DBC] Buy transaction failed on-chain:', confirmation.error);
        throw new Error(`Buy transaction failed: ${confirmation.error}`);
      }

      DEBUG && console.log('[Meteora DBC] ========================================');
      DEBUG && console.log('[Meteora DBC] BUY SUCCESSFUL!');
      DEBUG && console.log('[Meteora DBC] Tx Signature:', signature);
      DEBUG && console.log('[Meteora DBC] ========================================');

      return {
        txSignature: signature,
        amountIn: input.amountInSol,
        amountOut: quote.amountOut,
        priceImpact: quote.priceImpact,
        fee: quote.fee,
      };
    } catch (error) {
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] BUY FAILED');
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] Error:', error);
      
      // Check for specific error types to provide better messages
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (errorMessage.includes('Pool not found')) {
        // Keep the pool not found message as-is
        throw error;
      } else if (errorMessage.includes('User rejected')) {
        throw new Error('Transaction was rejected by user');
      } else if (errorMessage.includes('insufficient')) {
        throw new Error('Insufficient balance for this transaction');
      } else {
        // Generic error - log details to console
        console.error('[Meteora DBC] Full error details for debugging:', {
          mint: input.poolAddress,
          amountSol: input.amountInSol,
          slippage: input.slippageBps,
          errorMessage,
        });
        throw new Error(`Trade failed: ${errorMessage}. Check console for details.`);
      }
    }
  }

  /**
   * Sell tokens to the DBC pool
   * 
   * FEES:
   * - Protocol fees: Handled by the on-chain config (goes to protocol fee owner)
   * - Referral fees: Routed to treasury WSOL ATA via referralTokenAccount
   */
  async sellToCurve(
    input: SellToCurveInput,
    sendTransaction: (transaction: Transaction, connection: Connection) => Promise<string>
  ): Promise<TradeResult> {
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] SELL TO CURVE');
    DEBUG && console.log('[Meteora DBC] ========================================');
    DEBUG && console.log('[Meteora DBC] Input mint address:', input.poolAddress);
    DEBUG && console.log('[Meteora DBC] Amount in tokens:', input.amountInTokens);
    DEBUG && console.log('[Meteora DBC] Slippage BPS:', input.slippageBps);
    DEBUG && console.log('[Meteora DBC] User wallet:', input.userWallet.toBase58());
    DEBUG && console.log('[Meteora DBC] Treasury wallet:', DEXTORO_TREASURY_WALLET.toBase58());

    try {
      // Use cached pool state (0 RPC calls on cache hit)
      const cached = await this.getCachedPoolState(input.poolAddress);

      if (!cached) {
        console.error('[Meteora DBC] POOL NOT FOUND - cannot proceed with sell');
        throw new Error('Pool not found for this coin. Trading is not yet available.');
      }

      const poolAddress = cached.poolAddress;
      const amountInSmallest = new BN(Math.floor(input.amountInTokens * BASE_TOKEN_MULTIPLIER));

      DEBUG && console.log('[Meteora DBC] Pool found (cached):', poolAddress.toBase58());
      DEBUG && console.log('[Meteora DBC] Resolved config:', cached.poolState?.config?.toBase58?.() ?? 'unknown');

      // Use precomputed quote if available, otherwise fetch fresh
      const quote = input.precomputedQuote ?? await this.getSellQuote(input.poolAddress, input.amountInTokens, input.slippageBps);
      const minimumAmountOut = new BN(Math.floor(quote.minAmountOut * LAMPORTS_PER_SOL));

      DEBUG && console.log('[Meteora DBC] Quote:', {
        precomputed: !!input.precomputedQuote,
        amountOut: quote.amountOut,
        minAmountOut: quote.minAmountOut,
        priceImpact: quote.priceImpact,
        fee: quote.fee,
      });

      // Create the swap transaction
      const swapTx = await this.dbcClient.pool.swap({
        owner: input.userWallet,
        pool: poolAddress,
        amountIn: amountInSmallest,
        minimumAmountOut,
        swapBaseForQuote: true, // true = selling tokens for SOL
        referralTokenAccount: null,
      });

      // Use cached blockhash when available
      const { blockhash, lastValidBlockHeight } = await this.getCachedBlockhash();
      swapTx.recentBlockhash = blockhash;
      swapTx.feePayer = input.userWallet;

      // Add compute budget + Jito tip for Helius Sender dual routing
      await prepareTransactionForSender(swapTx, input.userWallet, {
        priorityLevel: input.priorityLevel,
      });

      DEBUG && console.log('[Meteora DBC] Transaction prepared with Sender instructions');
      DEBUG && console.log('[Meteora DBC] Instructions count:', swapTx.instructions.length);
      DEBUG && console.log('[Meteora DBC] Sending sell transaction...');

      const signature = await sendTransaction(swapTx, this.connection);
      DEBUG && console.log('[Meteora DBC] Sell tx sent:', signature);
      DEBUG && console.log('[Meteora DBC] Solscan:', getSolscanUrl(signature, 'tx'));

      // Notify caller before waiting for confirmation (enables optimistic UI)
      input.onSent?.(signature, quote);

      // Use HTTP-based confirmation with aggressive polling for trades
      const confirmation = await confirmSignatureHttp(this.connection, signature, {
        commitment: 'confirmed',
        timeoutMs: 120000,
        label: 'sell-swap',
        pollIntervalFn: tradingPollInterval,
      });

      if (!confirmation.confirmed) {
        if (confirmation.pending) {
          // Transaction may still confirm - provide signature for user to check
          console.warn('[Meteora DBC] Sell transaction confirmation timed out but may still succeed');
          throw new Error(`Transaction sent but confirmation timed out. Check signature: ${signature}`);
        }
        console.error('[Meteora DBC] Sell transaction failed on-chain:', confirmation.error);
        throw new Error(`Sell transaction failed: ${confirmation.error}`);
      }

      DEBUG && console.log('[Meteora DBC] ========================================');
      DEBUG && console.log('[Meteora DBC] SELL SUCCESSFUL!');
      DEBUG && console.log('[Meteora DBC] Tx Signature:', signature);
      DEBUG && console.log('[Meteora DBC] ========================================');

      return {
        txSignature: signature,
        amountIn: input.amountInTokens,
        amountOut: quote.amountOut,
        priceImpact: quote.priceImpact,
        fee: quote.fee,
      };
    } catch (error) {
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] SELL FAILED');
      console.error('[Meteora DBC] ========================================');
      console.error('[Meteora DBC] Error:', error);
      throw error;
    }
  }

  /**
   * Get transaction explorer URL
   */
  getExplorerUrl(signature: string): string {
    return getSolscanUrl(signature, 'tx');
  }

  /**
   * Get the connection object for external use
   */
  getConnection(): Connection {
    return this.connection;
  }

  /**
   * Get the DBC client for advanced usage
   */
  getDbcClient(): DynamicBondingCurveClient {
    return this.dbcClient;
  }
}

// Singleton instance
let clientInstance: MeteoraDBCClient | null = null;

export function getDBCClient(): MeteoraDBCClient {
  if (!clientInstance) {
    clientInstance = new MeteoraDBCClient(getSolanaEndpoint());
  }
  return clientInstance;
}

export * from './types';
